import { StateCreator } from 'zustand';
import { 
    AppState, 
    ClientDataState, 
    TeamMember, 
    ULMonatsDaten, 
    PrognoseZeile, 
    StoredState, 
    ProduktRechnerItem,
    PrivateCostState,
    PrivateCategory,
    PrivateItem,
    StaffDraftItem,
    NewStaffDraftItem,
    EntrepreneurDraftItem,
    CostDraftItem,
    ClientMasterDraftItem,
    OpeningHoursDraftItem
} from '../types';
import { 
    saveToLocalStorage, 
    generateSimpleId,
    generateTeamMemberId,
    forceNumber, 
    mergeArrayData, 
    loadFromLocalStorage 
} from '../utils';
import { applyUnproductiveSplitToPersonalLists } from '../../utils/personnelCostAdapter';
import {
    defaultSettings,
    defaultAppSettings,
    mergeWithDefaultSettings,
    normalizeLandCode,
    resolveActiveCountry,
    resolveEffectiveClientMwstSatz,
} from './settingsSlice';
import { isHj1WriteBlocked } from '../../utils/hjLock';

// --- HILFSFUNKTION ---
const parseValLocal = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        const clean = val.replace(/\./g, '').replace(',', '.');
        const num = parseFloat(clean);
        return isNaN(num) ? 0 : num;
    }
    return 0;
};

export const syncParamsToGlobal = (cData: any, currentSettings: any) => {
    if (!currentSettings) return currentSettings;
    if (cData && typeof cData === 'object') {
        const ustBefreit = Boolean(
            cData.umsatzsteuerbefreit === true ||
                cData.isUmsatzsteuerbefreit === true ||
                cData.isVatExempt === true
        );
        cData.umsatzsteuerbefreit = ustBefreit;
        cData.isVatExempt = ustBefreit;
    }
    const s = { ...currentSettings };
    if (!s.global) s.global = {};
    s.global = {
        ...s.global,
        Wochensatz: cData.wochensatz ?? 4.33,
        wochensatz: cData.wochensatz ?? 4.33,
        Auslastungsfaktor: cData.auslastungsfaktor ?? 75,
        Auslastungsfaktor_Max: cData.auslastungsfaktorMax ?? 100,
        Zusatzumsatz: cData.zusatzumsatz ?? 30,
        Gewinnlinie: cData.gewinnlinie ?? 26,
        Investlinie: cData.investlinie ?? 5,
        Verlust_1: cData.verlust1 ?? 15,
        Liquiditaet: cData.liquiditaet ?? 10,
    };
    // Land → Länderprofil (MwSt, Lohnnebenkosten, Mindestlohn) immer frisch
    const land = normalizeLandCode(cData?.land || cData?.Land || s.selectedCountryId);
    s.selectedCountryId = land;
    s.country = resolveActiveCountry(s, { ...cData, land });
    return s;
};

export const injectSalonDefizit = (cData: any, currentSettings: any): any => {
    if (!cData || !cData.gesamtleistungData) return cData;
    try {
        const mwstSatz = resolveEffectiveClientMwstSatz(currentSettings, cData);
        const activeMwstFaktor = 1 + (mwstSatz / 100);
        const lfData = cData.lohnfaktorData || {};
        
        const m1 = parseValLocal(lfData.mindestumsatz1);
        const m2 = parseValLocal(lfData.mindestumsatz2) || m1;

        let activeCount = 0;
        let totalRuecklagenDelta = 0;

        cData.gesamtleistungData.forEach((m: any, i: number) => {
            const b = parseValLocal(m.bedienumsatzBrutto ?? m.bedienUmsatz);
            const v = parseValLocal(m.vkUmsatzBrutto ?? m.vkUmsatz);
            const isLocked = m.abgeschlossen === true;

            if (b > 0 || v > 0 || isLocked) {
                activeCount++;
                const snap = m.frozenCalculations || {};
                
                const baseMindest = (isLocked && snap.mindestumsatzBase !== undefined) 
                    ? parseValLocal(snap.mindestumsatzBase) 
                    : (i < 6 ? m1 : m2);
                
                const anpassung = m.kostenErhoeht ? -1 : 1;
                const finalMindest = baseMindest + (parseValLocal(m.aussergewoehnlich) * anpassung);
                
                const mwst = (isLocked && snap.mwstFaktor !== undefined) 
                    ? parseValLocal(snap.mwstFaktor) 
                    : activeMwstFaktor;
                    
                const netto = mwst > 0 ? (b + v) / mwst : 0;
                const delta = netto - finalMindest;
                
                totalRuecklagenDelta += delta;
            }
        });

        if (activeCount > 0) {
            const avgDelta = totalRuecklagenDelta / activeCount;
            cData.isSalonDefizitaer = avgDelta < -0.5;
            cData.salonDefizitValue = avgDelta;
        } else {
            cData.isSalonDefizitaer = false;
            cData.salonDefizitValue = 0;
        }
    } catch (e) {
        console.error("Defizit-Berechnung fehlgeschlagen:", e);
    }
    return cData;
};

export const createDefaultULMonatsDaten = (monat: string): ULMonatsDaten => ({ 
    monat, 
    bedienumsatzBrutto: 0, 
    vkUmsatzBrutto: 0, 
    urlaubsTage: 0, 
    krankheitsTage: 0, 
    festmachen: false 
});

export const defaultULHalbjahr1 = (): ULMonatsDaten[] => [ 
    createDefaultULMonatsDaten("Januar"), 
    createDefaultULMonatsDaten("Februar"), 
    createDefaultULMonatsDaten("März"), 
    createDefaultULMonatsDaten("April"), 
    createDefaultULMonatsDaten("Mai"), 
    createDefaultULMonatsDaten("Juni") 
];

export const defaultULHalbjahr2 = (): ULMonatsDaten[] => [ 
    createDefaultULMonatsDaten("Juli"), 
    createDefaultULMonatsDaten("August"), 
    createDefaultULMonatsDaten("September"), 
    createDefaultULMonatsDaten("Oktober"), 
    createDefaultULMonatsDaten("November"), 
    createDefaultULMonatsDaten("Dezember") 
];

const defaultProdukte: ProduktRechnerItem[] = [
    { id: '1', name: 'Farbe Beispiel 100g Cramer', ek: 7.15, vk: 22.00 },
    { id: '2', name: 'Skaadi Bürste Natur', ek: 13.50, vk: 35.00 },
    { id: '3', name: 'Skaadi Bürste Kunststoff', ek: 6.00, vk: 15.60 },
    { id: '4', name: 'PM Shampoo', ek: 14.45, vk: 29.95 },
    { id: '5', name: 'PM Conditioner', ek: 15.45, vk: 31.95 },
    { id: '6', name: 'Kemon Shampoo', ek: 10.10, vk: 20.95 },
    { id: '7', name: 'Kemon Colore Brillante Mask', ek: 16.45, vk: 34.95 },
    { id: '8', name: 'LAUREA Basic Hairline 35 cm', ek: 168.06, vk: 476.00 },
    { id: '9', name: 'LAUREA Basic Hairline 45 cm', ek: 210.08, vk: 595.00 },
    { id: '10', name: 'LAUREA Basic Hairline 50 cm', ek: 231.09, vk: 654.00 },
    { id: '11', name: 'LAUREA Basic Hairline 55cm', ek: 252.10, vk: 713.00 },
    { id: '12', name: 'LAUREA Basic Hairline 60 cm', ek: 273.11, vk: 773.00 },
    { id: '13', name: 'LAUREA Natural Hairline Boost 35 cm', ek: 231.09, vk: 654.00 },
    { id: '14', name: 'LAUREA Natural Hairline Boost 45 cm', ek: 273.11, vk: 773.00 },
    { id: '15', name: 'LAUREA Natural Hairline Boost 50 cm', ek: 294.12, vk: 832.00 },
    { id: '16', name: 'LAUREA Natural Hairline Boost 55 cm', ek: 315.13, vk: 892.00 },
    { id: '17', name: 'LAUREA Natural Hairline Boost 60 cm', ek: 336.13, vk: 951.00 },
    { id: '18', name: 'Simplie Tresse à 35cm', ek: 68.00, vk: 159.80 },
    { id: '19', name: 'Simplie Tresse à 40cm', ek: 80.75, vk: 189.76 },
    { id: '20', name: 'Simplie Tresse à 45cm', ek: 89.25, vk: 209.74 },
    { id: '21', name: 'Simplie Tresse à 50cm', ek: 97.75, vk: 229.71 },
];

const defaultMonthlyCats: PrivateCategory[] = [
    {
        id: 'housing',
        type: 'living',
        title: '🏠 Wohnen & Energie',
        items: [
            { name: 'Private Miete (warm) / Rate', value: 0, fixed: true },
            { name: 'Strom & Heizung', value: 0, fixed: true },
            { name: 'Internet / Telefon', value: 0, fixed: true },
        ]
    },
    {
        id: 'mobility',
        type: 'living',
        title: '🚗 Mobilität (Privatanteil)',
        items: [
            { name: 'Leasing / Finanzierung', value: 0, fixed: true },
            { name: 'KFZ-Versicherung (monatl.)', value: 0, fixed: true },
            { name: 'Sprit / Laden', value: 0, fixed: true },
        ]
    },
    {
        id: 'social',
        type: 'social',
        title: '🛡️ Vorsorge & Gesundheit',
        items: [
            { name: 'Krankenversicherung (Privat)', value: 0, fixed: true },
            { name: 'Altersvorsorge', value: 0, fixed: true },
            { name: 'Berufsunfähigkeit (BU)', value: 0, fixed: true },
            { name: '', value: 0, fixed: false },
        ]
    },
    {
        id: 'lifestyle',
        type: 'living',
        title: '🛒 Lebenshaltung',
        items: [
            { name: 'Lebensmittel / Drogerie', value: 0, fixed: true },
            { name: 'Kleidung', value: 0, fixed: true },
            { name: 'Streaming / Abos', value: 0, fixed: true },
            { name: 'Sparen / Rücklagen', value: 0, fixed: true },
        ]
    }
];

const defaultPeriodicItems: PrivateItem[] = [
    { name: 'Rundfunkbeitrag (GEZ)', value: 0, frequency: 3, fixed: true }, 
    { name: 'KFZ-Steuer', value: 0, frequency: 12, fixed: true }, 
    { name: 'Haftpflicht (jährlich)', value: 0, frequency: 12, fixed: true },
    { name: 'Urlaubsgeld-Rücklage', value: 0, frequency: 12, fixed: true },
];

const defaultPrivateCostData: PrivateCostState = {
    targetLiving: 0,
    targetSocial: 0,
    monthlyCats: defaultMonthlyCats,
    periodicItems: defaultPeriodicItems
};

export const defaultClientData: ClientDataState & { frozenUntilMonth?: number } = { 
    firmenname: "", 
    mandantNr: "", 
    betreuer: "", 
    useAlternativeLogo: false, 
    isVatExempt: false, 
    isSoloBusiness: false, 
    soloVacationDays: 0,
    unternehmerData: { prodWochentage: 0, geoeffneteStunden: [0, 0, 0, 0, 0, 0] }, 
    bwaData: { zeitraum: 12, prognoseZeilen: [], visibleGeraeteCount: 4 }, 
    personalData: [], 
    unproductivePersonalData: [], 
    azubiAssistenzData: [], 
    teamBonus: { bonusMode: 'individual', poolAmount: 0, selectedMemberIds: [] },
    lohnfaktorData: {}, 
    mitarbeiterLeistung: {}, 
    gesamtleistungData: [], 
    gesamtleistungSettings: { planStundenTag: 8 }, 
    urlaubsverlustData: [], 
    ulBasisdaten: { simulationsStundenTag: 5.0 }, 
    ulHalbjahr1: defaultULHalbjahr1(), 
    ulHalbjahr2: defaultULHalbjahr2(), 
    cachedUnternehmerMetrics: { efficiency: 0, umsatzauslastung: 0, vkFaktor: 0, sumKrankTage: 0 }, 
    lizenzDetails: { typ: 'basic', laeuftAb: null }, 
    isHJ2Freigegeben: false, 
    isHJ1GesperrtGlobal: false, 
    isCostAdjustmentRequired: false, 
    unternehmername: '', 
    kontoinhaber: '', 
    kontoinhaberIsSameAsUnternehmer: false, 
    unternehmensform: '', 
    unternehmen: '', 
    salonname: '', 
    branchName: '',
    hasBranches: false,
    branches: [],
    activeBranchId: '',
    aggregatedNettoMonatsumsatz: 0,
    nettoMonatsumsatz: 0,
    isTeamProPlus: false,
    strasse: '', 
    plz: '', 
    ort: '', 
    mailadresse: '', 
    whatsappNr: '', 
    websiteUrl: '', 
    socialMedia: '', 
    iban: '', 
    auslandsteuerId: '', 
    sepaConfirmed: false, 
    contractConfirmed: false, 
    vertargsbeginn: '', 
    mindestlaufzeit: '', 
    insiderTeamleaderPlusChecked: false, 
    insiderTeamleaderStandardChecked: false, 
    insiderSoloChecked: false, 
    basicChecked: false, 
    proBonoChecked: false, 
    proBonoStartDate: '', 
    proBonoEndDate: '',

    personengesellschaft: { aktiv: false, partner: [] },

    unlockedPremiumTools: [],

    wochensatz: 4.33,
    auslastungsfaktor: 75,
    auslastungsfaktorMax: 100,
    zusatzumsatz: 30,
    gewinnlinie: 26,
    investlinie: 5,
    verlust1: 15,
    liquiditaet: 10,

    investitionData: {
        investition: 2800,
        laufzeit: 12,
        kundenbon: 900,
        mode: null,
        /** UI: Investition brutto oder netto eingeben — Speicherung immer netto */
        investitionEingabeModus: 'netto',
    },
    kalkulator: {
        inputs: { bisherigerPreis: 0, neuerPreis: 0, bedienzeit: 0, materialPreis: 0, materialGramm: 0 },
        selectedSurchargeId: '',
        activeMaterialIds: [],
        activeDeviceIds: [],
        manualGeraeteRates: {},
        barometerStundenfaktor: 0,
        barometerRuecklage: 0,
        barometerAnpassung: 0,
        hasConfirmedRates: false,
        priceParameterListe: [
            { id: 'p1', desc: '10g inkl. Arbeitszeit', preis: 0, ruecklage: 0, anpassung: 0 },
            { id: 'p2', desc: 'Ansatzfarbe 30g 45min.', preis: 0, ruecklage: 0, anpassung: 0 },
            { id: 'p3', desc: 'Ansatzfarbe 45g 45min.', preis: 0, ruecklage: 0, anpassung: 0 },
            { id: 'p4', desc: 'Stundensatz inkl. 50g Material', preis: 0, ruecklage: 0, anpassung: 0 },
        ],
    },
    extensionsKalkulator: {
        inputs: { menge: 2, stundensatz: 120, einarbeitungszeit: 60 },
        selectedExtensionId: 'lbh_35',
        extensions: [
            { id: 'lbh_35', name: 'LAUREA Basic Hairline 35 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 168.06, verkaufsMarge: 183 },
            { id: 'lbh_45', name: 'LAUREA Basic Hairline 45 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 210.08, verkaufsMarge: 183 },
            { id: 'lbh_50', name: 'LAUREA Basic Hairline 50 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 231.09, verkaufsMarge: 183 },
            { id: 'lbh_55', name: 'LAUREA Basic Hairline 55cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 252.10, verkaufsMarge: 183 },
            { id: 'lbh_60', name: 'LAUREA Basic Hairline 60 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 273.11, verkaufsMarge: 183 },
            { id: 'lnhb_35', name: 'LAUREA Natural Hairline Boost 35 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 231.09, verkaufsMarge: 183 },
            { id: 'lnhb_45', name: 'LAUREA Natural Hairline Boost 45 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 273.11, verkaufsMarge: 183 },
            { id: 'lnhb_50', name: 'LAUREA Natural Hairline Boost 50 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 294.12, verkaufsMarge: 183 },
            { id: 'lnhb_55', name: 'LAUREA Natural Hairline Boost 55 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 315.13, verkaufsMarge: 183 },
            { id: 'lnhb_60', name: 'LAUREA Natural Hairline Boost 60 cm', packContent: '1 St.', piecesPerPack: 1, ekNetto: 336.13, verkaufsMarge: 183 },
        ]
    },
    produktRechnerData: { 
        zielGewinnVk: '15,24',
        zielUeberschuss: '3,56',
        zielBedienzeit: '60',
        produkte: defaultProdukte
    },
    budgetPlaner: { zeitraum: '', entries: [] },
    privateCostData: defaultPrivateCostData,
    
    // --- VORJAHRES-DATEN INITIALISIEREN ---
    vorjahresBwaData: {},
    vorjahresUnternehmerData: {},
    vorjahresPersonalData: [],
    vorjahresCalcData: null,
    
    drafts: {
        existingStaff: {},
        newStaff: [],
        entrepreneur: [],
        clientMaster: [],
        costs: [],
        openingHours: [] 
    },

    aenderungsChronik: []
};
export const defaultTeam: TeamMember[] = [];
export const defaultTeamRegistry: TeamMember[] = [];

export const migrateTeamRegistry = (team: TeamMember[] = [], teamRegistry?: TeamMember[]): TeamMember[] => {
    if (Array.isArray(teamRegistry) && teamRegistry.length > 0) return teamRegistry;
    return [...(team || [])];
};

export const safeClone = (obj: any, fallback: any = {}) => {
    if (obj === undefined || obj === null) return fallback;
    try {
        const str = JSON.stringify(obj);
        if (str === undefined || str === 'undefined') return fallback;
        return JSON.parse(str);
    } catch (e) {
        return fallback;
    }
};

let lastPastSnapshotAt = 0;

export const getNewPast = (state: any) => {
    if (!state.isInitialized) return state.past;
    const now = Date.now();
    if (now - lastPastSnapshotAt < 600 && Array.isArray(state.past) && state.past.length > 0) {
        return state.past;
    }
    lastPastSnapshotAt = now;
    const newPast = [...state.past, { clientData: safeClone(state.clientData), settings: safeClone(state.settings) }];
    if (newPast.length > 20) newPast.shift();
    return newPast;
};

/**
 * Bewusste Personal-Änderung in DIESEM Tab markieren (Zeile gelöscht/geändert).
 * Nur mit dieser Markierung darf der Cloud-Sync eine leere Mitarbeiterliste
 * über eine befüllte Server-Liste schreiben (siehe Gift-Schutz in firebaseSync).
 */
const STAFF_TABLE_KEYS = new Set(['personalData', 'unproductivePersonalData', 'azubiAssistenzData']);
export const markStaffTableEdit = (key: unknown) => {
    try {
        if (STAFF_TABLE_KEYS.has(String(key))) {
            (window as any).__peStaffEditAt = Date.now();
        }
    } catch (_) { /* ignore */ }
};

const ensureArr = (val: any, fallback: any[] = []) => {
    if (Array.isArray(val)) return val.length > 0 ? val : fallback;
    if (typeof val === 'object' && val !== null) {
        const arr = Object.values(val);
        return arr.length > 0 ? arr : fallback;
    }
    return fallback;
};

export const parseLeistungSnapshotStrings = (empData: any) => {
    if (!empData || typeof empData !== 'object') return;

    if (typeof empData.frozenStammdatenHJ1_string === 'string' && empData.frozenStammdatenHJ1_string.trim()) {
        try {
            const parsed = JSON.parse(empData.frozenStammdatenHJ1_string);
            if (parsed && typeof parsed === 'object') {
                empData.frozenStammdatenHJ1 = parsed;
            }
        } catch {
            // ignore invalid JSON
        }
    }

    if (typeof empData.lockedMetrics_string === 'string' && empData.lockedMetrics_string.trim()) {
        try {
            const parsed = JSON.parse(empData.lockedMetrics_string);
            if (parsed && typeof parsed === 'object') {
                empData.lockedMetrics = parsed;
            }
        } catch {
            // ignore invalid JSON
        }
    }
};

export const isLegacyLockedMetrics = (lockedMetrics: any): boolean =>
    Boolean(lockedMetrics && typeof lockedMetrics.isLocked === 'boolean' && lockedMetrics.metrics);

export const resolveLeistungLockedMetricsIndex = (leistung: any): Record<string, any> => {
    if (!leistung || typeof leistung !== 'object') return {};
    if (isLegacyLockedMetrics(leistung.lockedMetrics)) return {};
    if (leistung.lockedMetrics && typeof leistung.lockedMetrics === 'object') {
        return leistung.lockedMetrics;
    }
    if (typeof leistung.lockedMetrics_string === 'string' && leistung.lockedMetrics_string.trim()) {
        try {
            const parsed = JSON.parse(leistung.lockedMetrics_string);
            if (parsed && typeof parsed === 'object' && !parsed.isLocked) {
                return parsed;
            }
        } catch {
            // ignore invalid JSON
        }
    }
    return {};
};

export const parseUnternehmerLockedMetricsFromStrings = (unternehmerData: any) => {
    if (!unternehmerData || typeof unternehmerData !== 'object') return undefined;

    let lockedMetrics = unternehmerData.unternehmerLockedMetrics || unternehmerData.lockedMetrics;

    if (typeof unternehmerData.unternehmerLockedMetrics_string === 'string' && unternehmerData.unternehmerLockedMetrics_string.trim()) {
        try {
            const parsed = JSON.parse(unternehmerData.unternehmerLockedMetrics_string);
            if (parsed && typeof parsed === 'object') {
                lockedMetrics = parsed;
            }
        } catch {
            // ignore invalid JSON
        }
    }

    return lockedMetrics;
};

export const stringifyLeistungSnapshotStrings = (empData: any) => {
    if (!empData || typeof empData !== 'object') return;

    if (empData.frozenStammdatenHJ1 && typeof empData.frozenStammdatenHJ1 === 'object') {
        try {
            empData.frozenStammdatenHJ1_string = JSON.stringify(empData.frozenStammdatenHJ1);
        } catch {
            delete empData.frozenStammdatenHJ1_string;
        }
    } else {
        delete empData.frozenStammdatenHJ1_string;
    }

    if (empData.lockedMetrics && typeof empData.lockedMetrics === 'object') {
        try {
            empData.lockedMetrics_string = JSON.stringify(empData.lockedMetrics);
        } catch {
            delete empData.lockedMetrics_string;
        }
    } else {
        delete empData.lockedMetrics_string;
    }
};

export const syncLeistungSnapshotStringsInClientData = (clientData: any) => {
    if (!clientData?.mitarbeiterLeistung) return clientData;
    Object.keys(clientData.mitarbeiterLeistung).forEach((empId) => {
        stringifyLeistungSnapshotStrings(clientData.mitarbeiterLeistung[empId]);
    });
    return clientData;
};

export const buildUnternehmerLockedMetricsFromUL = (ulHalbjahr1: any[]) => {
    const halbjahr1: Record<string, any> = {};
    (ulHalbjahr1 || []).forEach((month, index) => {
        if (month?.festmachen) {
            halbjahr1[String(index)] = {
                festmachen: true,
                lockedSnapshot: month.lockedSnapshot,
            };
        }
    });
    return Object.keys(halbjahr1).length > 0 ? { halbjahr1 } : undefined;
};

export const applyUnternehmerLockedMetricsToUL = (clientData: any) => {
    if (!clientData) return clientData;

    const lockedMetrics = parseUnternehmerLockedMetricsFromStrings(clientData.unternehmerData);

    if (!lockedMetrics?.halbjahr1 || typeof lockedMetrics.halbjahr1 !== 'object') {
        return clientData;
    }

    const ul1 = [...(clientData.ulHalbjahr1 || [])];
    Object.keys(lockedMetrics.halbjahr1).forEach((idxKey) => {
        const entry = lockedMetrics.halbjahr1[idxKey];
        const idx = Number(idxKey);
        if (!ul1[idx] || !entry) return;
        ul1[idx] = {
            ...ul1[idx],
            festmachen: true,
            lockedSnapshot: entry.lockedSnapshot ?? ul1[idx].lockedSnapshot,
        };
    });

    return { ...clientData, ulHalbjahr1: ul1 };
};

export const syncUnternehmerSnapshotStrings = (clientData: any) => {
    if (!clientData) return clientData;

    const locked = buildUnternehmerLockedMetricsFromUL(clientData.ulHalbjahr1);
    const unternehmerData = { ...(clientData.unternehmerData || {}) };

    if (locked) {
        unternehmerData.unternehmerLockedMetrics = locked;
        try {
            unternehmerData.unternehmerLockedMetrics_string = JSON.stringify(locked);
        } catch {
            delete unternehmerData.unternehmerLockedMetrics_string;
        }
        unternehmerData.lockedMetrics = locked;
    } else {
        delete unternehmerData.unternehmerLockedMetrics;
        delete unternehmerData.unternehmerLockedMetrics_string;
        delete unternehmerData.lockedMetrics;
    }

    return { ...clientData, unternehmerData };
};

/** Cloud-Upload: nur String-Felder, keine verschachtelten Snapshot-Objekte */
export const serializeClientDataForCloud = (clientData: any) => {
    let serialized: any;
    try {
        serialized = JSON.parse(JSON.stringify(clientData));
    } catch {
        return clientData;
    }

    const ml = serialized?.mitarbeiterLeistung;
    if (ml && typeof ml === 'object') {
        Object.keys(ml).forEach((empId) => {
            const emp = ml[empId];
            if (!emp) return;
            stringifyLeistungSnapshotStrings(emp);
            delete emp.frozenStammdatenHJ1;
            delete emp.lockedMetrics;
        });
    }

    const withUnternehmerStrings = syncUnternehmerSnapshotStrings(serialized);
    if (withUnternehmerStrings.unternehmerData) {
        serialized.unternehmerData = withUnternehmerStrings.unternehmerData;
        delete serialized.unternehmerData.unternehmerLockedMetrics;
        delete serialized.unternehmerData.lockedMetrics;
    }

    if (serialized.frozenUnternehmerStammdatenHJ1 && typeof serialized.frozenUnternehmerStammdatenHJ1 === 'object') {
        try {
            serialized.frozenUnternehmerStammdatenHJ1_string = JSON.stringify(serialized.frozenUnternehmerStammdatenHJ1);
        } catch {
            delete serialized.frozenUnternehmerStammdatenHJ1_string;
        }
        delete serialized.frozenUnternehmerStammdatenHJ1;
    }

    return serialized;
};

export const sanitizeClientData = (data: any) => {
    const fallback = safeClone(defaultClientData);
    if (!data || typeof data !== 'object') return fallback;

    const cleanData = { ...fallback, ...data };

    Object.keys(cleanData).forEach(key => {
        if (cleanData[key] === undefined || cleanData[key] === null) {
            cleanData[key] = (fallback as any)[key];
        }
    });

    cleanData.personengesellschaft = cleanData.personengesellschaft || { aktiv: false, partner: [] };

    const ustBefreit = Boolean(
        cleanData.umsatzsteuerbefreit === true ||
            cleanData.isUmsatzsteuerbefreit === true ||
            cleanData.isVatExempt === true
    );
    cleanData.umsatzsteuerbefreit = ustBefreit;
    cleanData.isVatExempt = ustBefreit;

    cleanData.wochensatz = cleanData.wochensatz ?? 4.33;
    cleanData.auslastungsfaktor = cleanData.auslastungsfaktor ?? 75;
    cleanData.auslastungsfaktorMax = cleanData.auslastungsfaktorMax ?? 100;
    cleanData.zusatzumsatz = cleanData.zusatzumsatz ?? 30;
    cleanData.gewinnlinie = cleanData.gewinnlinie ?? 26;
    cleanData.investlinie = cleanData.investlinie ?? 5;
    cleanData.verlust1 = cleanData.verlust1 ?? 15;
    cleanData.liquiditaet = cleanData.liquiditaet ?? 10;

    const cleanULArray = (arr: any[]) => {
        if (!Array.isArray(arr)) return [];
        return arr.map((m: any) => ({
            ...m,
            bedienumsatzBrutto: forceNumber(m.bedienumsatzBrutto ?? m.bedienUmsatz),
            vkUmsatzBrutto: forceNumber(m.vkUmsatzBrutto ?? m.vkUmsatz),
            urlaubsTage: forceNumber(m.urlaubsTage ?? m.urlaubstage),
            krankheitsTage: forceNumber(m.krankheitsTage ?? m.krankheitstage)
        }));
    };

    cleanData.ulHalbjahr1 = mergeArrayData(defaultULHalbjahr1(), cleanULArray(cleanData.ulHalbjahr1 || []), 'monat');
    cleanData.ulHalbjahr2 = mergeArrayData(defaultULHalbjahr2(), cleanULArray(cleanData.ulHalbjahr2 || []), 'monat');
    cleanData.gesamtleistungData = cleanULArray(cleanData.gesamtleistungData || []);

    const withUnternehmerSnapshots = applyUnternehmerLockedMetricsToUL(cleanData);
    cleanData.ulHalbjahr1 = withUnternehmerSnapshots.ulHalbjahr1;
    cleanData.unternehmerData = syncUnternehmerSnapshotStrings(cleanData).unternehmerData;

    if (typeof cleanData.frozenUnternehmerStammdatenHJ1_string === 'string' && cleanData.frozenUnternehmerStammdatenHJ1_string.trim()) {
        try {
            const parsed = JSON.parse(cleanData.frozenUnternehmerStammdatenHJ1_string);
            if (parsed && typeof parsed === 'object') {
                cleanData.frozenUnternehmerStammdatenHJ1 = parsed;
            }
        } catch {
            /* ignore */
        }
    }

    if (cleanData.mitarbeiterLeistung) {
        const mlClean = { ...cleanData.mitarbeiterLeistung };
        Object.keys(mlClean).forEach(empId => {
            const empData = mlClean[empId];
            if (empData) {
                parseLeistungSnapshotStrings(empData);

                if (Array.isArray(empData.halbjahr1)) {
                    empData.halbjahr1 = cleanULArray(empData.halbjahr1);
                }
                if (Array.isArray(empData.halbjahr2)) {
                    empData.halbjahr2 = cleanULArray(empData.halbjahr2);
                }
                if (empData.frozenStammdatenHJ1) {
                    const frozen = empData.frozenStammdatenHJ1;
                    empData.frozenStammdatenHJ1 = {
                        ...frozen,
                        name: frozen.name || '',
                        wochenstunden: forceNumber(frozen.wochenstunden),
                        arbeitstage: forceNumber(frozen.arbeitstage),
                        grundgehalt: forceNumber(frozen.grundgehalt),
                        lohnzusatz: forceNumber(frozen.lohnzusatz),
                        bonus: forceNumber(frozen.bonus),
                        taetigkeit: frozen.taetigkeit || '',
                        zugehoerigkeit: frozen.zugehoerigkeit || '',
                        arbeitetMitGeraeten: Boolean(frozen.arbeitetMitGeraeten),
                        selectedDeviceIds: Array.isArray(frozen.selectedDeviceIds) ? frozen.selectedDeviceIds : frozen.selectedDeviceIds,
                        lohnfaktorAktiv: frozen.lohnfaktorAktiv != null ? forceNumber(frozen.lohnfaktorAktiv) : undefined,
                        kostendeckungsfaktor1: frozen.kostendeckungsfaktor1 != null ? forceNumber(frozen.kostendeckungsfaktor1) : undefined,
                        provisionErstesHalbjahr: frozen.provisionErstesHalbjahr || { provisionDienstleistung: 10, provisionVerkauf: 10 },
                    };
                }
                if (empData.lockedMetrics) {
                    const lm = empData.lockedMetrics;
                    if (lm.isLocked === true && lm.metrics) {
                        empData.lockedMetrics = lm;
                    } else {
                        const normalizedIndex: Record<string, Record<string, any>> = {};
                        ['halbjahr1', 'halbjahr2'].forEach((hjKey) => {
                            if (lm[hjKey] && typeof lm[hjKey] === 'object') {
                                normalizedIndex[hjKey] = {};
                                Object.keys(lm[hjKey]).forEach((idx) => {
                                    const snap = lm[hjKey][idx];
                                    if (snap) {
                                        normalizedIndex[hjKey][idx] = {
                                            ...snap,
                                            arbeitstage: forceNumber(snap.arbeitstage),
                                            wochenstunden: forceNumber(snap.wochenstunden),
                                            lohnfaktor: forceNumber(snap.lohnfaktor),
                                            sollUmsatzMonat: forceNumber(snap.sollUmsatzMonat),
                                            effektiverBruttolohn: forceNumber(snap.effektiverBruttolohn),
                                            stdLohnGrund: forceNumber(snap.stdLohnGrund),
                                            stdLohnEffektiv: forceNumber(snap.stdLohnEffektiv),
                                            stdLohnGesamt: forceNumber(snap.stdLohnGesamt),
                                        };
                                    }
                                });
                            }
                        });
                        empData.lockedMetrics = Object.keys(normalizedIndex).length > 0 ? normalizedIndex : lm;
                    }
                }

                stringifyLeistungSnapshotStrings(empData);
            }
        });
        cleanData.mitarbeiterLeistung = mlClean;
    }

    cleanData.personalData = ensureArr(cleanData.personalData);
    cleanData.urlaubsverlustData = ensureArr(cleanData.urlaubsverlustData);
    if (cleanData.bwaData) {
        cleanData.bwaData.prognoseZeilen = ensureArr(cleanData.bwaData.prognoseZeilen);
    }

    // HJ1 darf nur gesperrt sein, wenn HJ2 freigeschaltet ist.
    // Sonst: Freigabe verloren, Sperre hängen geblieben → Akte komplett blockiert.
    if (cleanData.isHJ1GesperrtGlobal && !cleanData.isHJ2Freigegeben) {
        cleanData.isHJ1GesperrtGlobal = false;
        delete cleanData.frozenUnternehmerStammdatenHJ1;
        delete cleanData.frozenUnternehmerStammdatenHJ1_string;
        if (cleanData.mitarbeiterLeistung) {
            const ml = { ...cleanData.mitarbeiterLeistung };
            Object.keys(ml).forEach((empId) => {
                if (!ml[empId]) return;
                const cleaned = { ...ml[empId] };
                delete cleaned.frozenStammdatenHJ1;
                delete cleaned.frozenStammdatenHJ1_string;
                ml[empId] = cleaned;
            });
            cleanData.mitarbeiterLeistung = ml;
        }
    }

    return cleanData;
};

const getPerformanceLockDate = () => {
    const d = new Date();
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.`;
};

const mergeLockedMetricsPreferRicher = (cloudLm: any, localLm: any) => {
    if (!localLm) return cloudLm;
    if (!cloudLm) return localLm;
    if (localLm.isLocked === true && localLm.metrics) return localLm;
    if (cloudLm.isLocked === true && cloudLm.metrics && !localLm.halbjahr1 && !localLm.halbjahr2) {
        return localLm.halbjahr1 || localLm.halbjahr2 ? localLm : cloudLm;
    }
    const merged: Record<string, any> = { ...cloudLm };
    ['halbjahr1', 'halbjahr2'].forEach((hjKey) => {
        if (localLm[hjKey] && typeof localLm[hjKey] === 'object') {
            merged[hjKey] = { ...(merged[hjKey] || {}), ...localLm[hjKey] };
        }
    });
    return merged;
};

const PENDING_FESTSETZUNGEN_KEY = 'PE_PENDING_FESTSETZUNGEN';

export const stashPendingFestsetzung = (
    employeeId: string,
    halbjahrKey: 'halbjahr1' | 'halbjahr2',
    monatIndex: number,
    snapshot: Record<string, any>
) => {
    if (typeof window === 'undefined') return;
    try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_FESTSETZUNGEN_KEY) || '{}');
        pending[`${employeeId}|${halbjahrKey}|${monatIndex}`] = {
            employeeId,
            halbjahrKey,
            monatIndex,
            snapshot,
            festmachen: true,
            savedAt: Date.now(),
        };
        sessionStorage.setItem(PENDING_FESTSETZUNGEN_KEY, JSON.stringify(pending));
    } catch {
        // ignore quota / parse errors
    }
};

export const clearPendingFestsetzung = (
    employeeId: string,
    halbjahrKey: 'halbjahr1' | 'halbjahr2',
    monatIndex: number
) => {
    if (typeof window === 'undefined') return;
    try {
        const pending = JSON.parse(sessionStorage.getItem(PENDING_FESTSETZUNGEN_KEY) || '{}');
        delete pending[`${employeeId}|${halbjahrKey}|${monatIndex}`];
        sessionStorage.setItem(PENDING_FESTSETZUNGEN_KEY, JSON.stringify(pending));
    } catch {
        // ignore
    }
};

export const applyPendingFestsetzungen = (clientData: any) => {
    if (typeof window === 'undefined' || !clientData) return clientData;
    try {
        const raw = sessionStorage.getItem(PENDING_FESTSETZUNGEN_KEY);
        if (!raw) return clientData;

        const pending = JSON.parse(raw);
        const entries = Object.values(pending) as Array<{
            employeeId: string;
            halbjahrKey: 'halbjahr1' | 'halbjahr2';
            monatIndex: number;
            snapshot: Record<string, any>;
            festmachen?: boolean;
        }>;
        if (!entries.length) return clientData;

        const merged = { ...clientData };
        const mergedML = { ...(merged.mitarbeiterLeistung || {}) };

        entries.forEach((entry) => {
            if (!entry?.employeeId || entry.halbjahrKey === undefined || entry.monatIndex === undefined) return;

            const cloudEmp = mergedML[entry.employeeId] || {};
            const hjKey = entry.halbjahrKey;
            const idxKey = String(entry.monatIndex);
            const hjArr = [...(cloudEmp[hjKey] || [])];

            if (hjArr[entry.monatIndex]) {
                hjArr[entry.monatIndex] = { ...hjArr[entry.monatIndex], festmachen: entry.festmachen ?? true };
            }

            const lockedMetrics = { ...(cloudEmp.lockedMetrics || {}) };
            if (lockedMetrics.isLocked === true && lockedMetrics.metrics) {
                // legacy format — do not overwrite with pending index data
            } else {
                const hjLocked = { ...(lockedMetrics[hjKey] || {}) };
                hjLocked[idxKey] = {
                    isLocked: true,
                    lockedAt: getPerformanceLockDate(),
                    lockedBy: 'Admin',
                    ...entry.snapshot,
                };
                lockedMetrics[hjKey] = hjLocked;
            }

            mergedML[entry.employeeId] = {
                ...cloudEmp,
                [hjKey]: hjArr,
                lockedMetrics: lockedMetrics.isLocked === true && lockedMetrics.metrics
                    ? lockedMetrics
                    : lockedMetrics,
            };
        });

        merged.mitarbeiterLeistung = mergedML;
        return merged;
    } catch {
        return clientData;
    }
};

const mergeFestmachenAwareLockedMetrics = (cloudEmp: any, localEmp: any) => {
    let lockedMetrics = mergeLockedMetricsPreferRicher(cloudEmp?.lockedMetrics, localEmp?.lockedMetrics);

    ['halbjahr1', 'halbjahr2'].forEach((hjKey) => {
        const localMonths = localEmp?.[hjKey];
        if (!Array.isArray(localMonths)) return;

        localMonths.forEach((month: any, index: number) => {
            if (!month?.festmachen) return;
            const idxKey = String(index);
            const localSnap = localEmp?.lockedMetrics?.[hjKey]?.[idxKey];
            const cloudSnap = lockedMetrics?.[hjKey]?.[idxKey];
            if (localSnap && !cloudSnap) {
                if (!lockedMetrics || lockedMetrics.isLocked === true) {
                    lockedMetrics = {};
                }
                if (!lockedMetrics[hjKey]) lockedMetrics[hjKey] = {};
                lockedMetrics[hjKey][idxKey] = localSnap;
            }
        });
    });

    return Object.keys(lockedMetrics || {}).length > 0 ? lockedMetrics : undefined;
};

export const mergeLocalLeistungSnapshots = (cloudClientData: any) => {
    const localState = loadFromLocalStorage();
    if (!localState?.clientData?.mitarbeiterLeistung) {
        return applyPendingFestsetzungen(cloudClientData);
    }

    const merged = { ...cloudClientData };
    const mergedML = { ...(merged.mitarbeiterLeistung || {}) };

    Object.keys(localState.clientData.mitarbeiterLeistung).forEach((empId) => {
        const localEmp = localState.clientData.mitarbeiterLeistung[empId];
        if (!localEmp) return;

        const cloudEmp = mergedML[empId] || {};
        const mergedEmp: any = { ...cloudEmp };

        parseLeistungSnapshotStrings(localEmp);
        parseLeistungSnapshotStrings(mergedEmp);

        if (localEmp.lockedMetrics || cloudEmp.lockedMetrics || localEmp.halbjahr1 || localEmp.halbjahr2) {
            mergedEmp.lockedMetrics = mergeFestmachenAwareLockedMetrics(cloudEmp, localEmp);
        }

        if (localEmp.frozenStammdatenHJ1 && !cloudEmp.frozenStammdatenHJ1) {
            mergedEmp.frozenStammdatenHJ1 = localEmp.frozenStammdatenHJ1;
            mergedEmp.frozenStammdatenHJ1_string = localEmp.frozenStammdatenHJ1_string
                || JSON.stringify(localEmp.frozenStammdatenHJ1);
        }

        const mergeFestmachenFlags = (cloudMonths: any[], localMonths: any[]) => {
            if (!Array.isArray(cloudMonths) || !Array.isArray(localMonths)) return cloudMonths;
            return cloudMonths.map((month, index) => {
                const localMonth = localMonths[index];
                if (localMonth?.festmachen && !month?.festmachen) {
                    return { ...month, festmachen: true };
                }
                return month;
            });
        };

        mergedEmp.halbjahr1 = mergeFestmachenFlags(cloudEmp.halbjahr1, localEmp.halbjahr1);
        mergedEmp.halbjahr2 = mergeFestmachenFlags(cloudEmp.halbjahr2, localEmp.halbjahr2);

        mergedML[empId] = mergedEmp;
    });

    merged.mitarbeiterLeistung = mergedML;

    // isHJ1GesperrtGlobal: Cloud/Store-Wert gilt — kein Sticky-true aus Local (sonst bleibt HJ1 ewig gesperrt)

    return applyPendingFestsetzungen(merged);
};

export const LOCAL_FESTSETZUNGEN_KEY = 'PE_FESTSETZUNGEN_V1';

const parseSnapshotValue = (value: any) => {
    if (!value) return null;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return value;
};

export const injectFestsetzungsSnapshots = (clientData: any, festsetzungsSnapshots: any): any => {
    if (!festsetzungsSnapshots || typeof festsetzungsSnapshots !== 'object' || !clientData) {
        return clientData;
    }

    const merged = { ...clientData };
    const mergedML = { ...(merged.mitarbeiterLeistung || {}) };

    Object.keys(festsetzungsSnapshots).forEach((empId) => {
        const empSnaps = festsetzungsSnapshots[empId];
        if (!empSnaps || typeof empSnaps !== 'object') return;

        const empLeistung: any = { ...(mergedML[empId] || {}) };
        const rawLocked = empLeistung.lockedMetrics;
        const isLegacy = Boolean(rawLocked && rawLocked.isLocked === true && rawLocked.metrics);

        if (!isLegacy) {
            const lockedMetrics: Record<string, any> = (rawLocked && !rawLocked.isLocked)
                ? JSON.parse(JSON.stringify(rawLocked))
                : {};

            ['halbjahr1', 'halbjahr2'].forEach((hjKey) => {
                const hjSnaps = empSnaps[hjKey];
                if (!hjSnaps || typeof hjSnaps !== 'object') return;

                const hjArr = [...(empLeistung[hjKey] || [])];
                if (!lockedMetrics[hjKey]) lockedMetrics[hjKey] = {};

                Object.keys(hjSnaps).forEach((idxKey) => {
                    const snap = parseSnapshotValue(hjSnaps[idxKey]);
                    if (!snap) return;
                    lockedMetrics[hjKey][idxKey] = { ...snap };

                    const monthIdx = Number(idxKey);
                    if (hjArr[monthIdx]) {
                        hjArr[monthIdx] = { ...hjArr[monthIdx], festmachen: true };
                    }
                });

                empLeistung[hjKey] = hjArr;
            });

            if (Object.keys(lockedMetrics).length > 0) {
                empLeistung.lockedMetrics = lockedMetrics;
            }
        }

        mergedML[empId] = empLeistung;
    });

    merged.mitarbeiterLeistung = mergedML;
    return merged;
};

export const saveLocalFestsetzungBackup = (
    employeeId: string,
    halbjahrKey: 'halbjahr1' | 'halbjahr2',
    monatIndex: number,
    snapshot: Record<string, any>,
    meta?: { lockedAt?: string; lockedBy?: string }
) => {
    if (typeof window === 'undefined') return;
    try {
        const all = JSON.parse(localStorage.getItem(LOCAL_FESTSETZUNGEN_KEY) || '{}');
        if (!all[employeeId]) all[employeeId] = { halbjahr1: {}, halbjahr2: {} };
        if (!all[employeeId][halbjahrKey]) all[employeeId][halbjahrKey] = {};
        all[employeeId][halbjahrKey][String(monatIndex)] = {
            ...snapshot,
            isLocked: true,
            lockedAt: meta?.lockedAt,
            lockedBy: meta?.lockedBy || 'Admin',
        };
        localStorage.setItem(LOCAL_FESTSETZUNGEN_KEY, JSON.stringify(all));
    } catch {
        // ignore
    }
};

export const removeLocalFestsetzungBackup = (
    employeeId: string,
    halbjahrKey: 'halbjahr1' | 'halbjahr2',
    monatIndex: number
) => {
    if (typeof window === 'undefined') return;
    try {
        const all = JSON.parse(localStorage.getItem(LOCAL_FESTSETZUNGEN_KEY) || '{}');
        if (all[employeeId]?.[halbjahrKey]) {
            delete all[employeeId][halbjahrKey][String(monatIndex)];
        }
        localStorage.setItem(LOCAL_FESTSETZUNGEN_KEY, JSON.stringify(all));
    } catch {
        // ignore
    }
};

export const applyLocalFestsetzungBackup = (clientData: any) => {
    if (typeof window === 'undefined' || !clientData) return clientData;
    try {
        const raw = localStorage.getItem(LOCAL_FESTSETZUNGEN_KEY);
        if (!raw) return clientData;
        return injectFestsetzungsSnapshots(clientData, JSON.parse(raw));
    } catch {
        return clientData;
    }
};

const normalizeMonatsFestsetzungSnapshot = (metricsSnapshot: Record<string, any>) => {
    const basis = metricsSnapshot.basisMetrics || {};
    const effektivBrutto = Number(metricsSnapshot.effektiverBruttolohn ?? basis.effektiverBruttolohn) || 0;
    const sollUmsatz = Number(metricsSnapshot.sollUmsatzMonat ?? basis.sollUmsatzMonat) || 0;
    const lohnfaktorRaw = metricsSnapshot.lohnfaktor ?? basis.lohnfaktor;
    const lohnfaktor = Number(lohnfaktorRaw) || (effektivBrutto > 0 ? sollUmsatz / effektivBrutto : 0);

    return {
        arbeitstage: Number(metricsSnapshot.arbeitstage ?? basis.arbeitstage) || 0,
        wochenstunden: Number(metricsSnapshot.wochenstunden ?? basis.wochenstunden) || 0,
        lohnfaktor,
        sollUmsatzMonat: sollUmsatz,
        effektiverBruttolohn: effektivBrutto,
        stdLohnGrund: Number(metricsSnapshot.stdLohnGrund ?? basis.stdLohnGrund) || 0,
        stdLohnEffektiv: Number(metricsSnapshot.stdLohnEffektiv ?? basis.stdLohnEffektiv) || 0,
        stdLohnGesamt: Number(metricsSnapshot.stdLohnGesamt ?? basis.stdLohnGesamt) || 0,
        basisMetrics: metricsSnapshot.basisMetrics,
        bonusFaktoren: metricsSnapshot.bonusFaktoren,
    };
};

const applyPerformanceLockToClientData = (
    clientData: ClientDataState,
    domain: 'mitarbeiter' | 'unternehmer' | 'gesamt',
    targetId: string | null,
    isLocked: boolean,
    metricsSnapshot?: Record<string, any>,
    adminId?: string,
    lockContext?: { halbjahrKey: 'halbjahr1' | 'halbjahr2'; monatIndex: number }
) => {
    let newClientData = { ...clientData };

    if (domain === 'mitarbeiter' && targetId && lockContext?.halbjahrKey !== undefined && lockContext?.monatIndex !== undefined) {
        const ml = { ...(newClientData.mitarbeiterLeistung || {}) };
        const existing = ml[targetId] || {};
        parseLeistungSnapshotStrings(existing);
        const rawLocked = existing.lockedMetrics;
        const existingLocked: Record<string, Record<string, any>> = (rawLocked && !rawLocked.isLocked)
            ? JSON.parse(JSON.stringify(rawLocked))
            : {};

        const hjKey = lockContext.halbjahrKey;
        const idxKey = String(lockContext.monatIndex);
        const hjLocked = { ...(existingLocked[hjKey] || {}) };
        const hjArr = [...(existing[hjKey] || [])];

        if (hjArr[lockContext.monatIndex]) {
            hjArr[lockContext.monatIndex] = {
                ...hjArr[lockContext.monatIndex],
                festmachen: isLocked,
            };
        }

        if (isLocked && metricsSnapshot) {
            hjLocked[idxKey] = {
                isLocked: true,
                lockedAt: getPerformanceLockDate(),
                lockedBy: adminId || 'Admin',
                ...normalizeMonatsFestsetzungSnapshot(metricsSnapshot),
            };
        } else {
            delete hjLocked[idxKey];
        }

        const newLockedMetrics = { ...existingLocked };
        if (Object.keys(hjLocked).length > 0) {
            newLockedMetrics[hjKey] = hjLocked;
        } else {
            delete newLockedMetrics[hjKey];
        }

        ml[targetId] = {
            ...existing,
            [hjKey]: hjArr,
            lockedMetrics: Object.keys(newLockedMetrics).length > 0 ? newLockedMetrics : undefined,
        };
        newClientData.mitarbeiterLeistung = ml;
    } else {
        const lockedObj = isLocked ? {
            isLocked: true,
            lockedAt: getPerformanceLockDate(),
            lockedBy: adminId || 'Admin',
            metrics: metricsSnapshot || {}
        } : undefined;

        if (domain === 'mitarbeiter' && targetId) {
            const ml = { ...(newClientData.mitarbeiterLeistung || {}) };
            ml[targetId] = { ...(ml[targetId] || {}), lockedMetrics: lockedObj };
            newClientData.mitarbeiterLeistung = ml;
        } else if (domain === 'unternehmer') {
            newClientData.unternehmerData = { ...newClientData.unternehmerData, lockedMetrics: lockedObj };
        } else if (domain === 'gesamt') {
            newClientData.gesamtleistungLockedMetrics = lockedObj;
        }
    }

    syncLeistungSnapshotStringsInClientData(newClientData);
    return newClientData;
};

const calculateRowMetrics = (inputs: StaffDraftItem['inputs'] | NewStaffDraftItem['inputs'], settings: any) => {
    const { global, country } = settings;
    const landCode = normalizeLandCode(settings?.selectedCountryId || 'DE');
    const wochensatz = global?.Wochensatz || 4.33; 
    const minWage = country?.Mindestlohn || 0;
    const miniJobLimit = country?.Minijob_Lohn_Grenze || 538;
    const svStandard =
        country?.Lohnnebenkosten ??
        (landCode === 'CH' ? 17 : landCode === 'AT' ? 52.0 : landCode === 'IT' ? 80 : 22.5);
    const svMini = country?.Lohnnebenkosten_Minijob ?? (landCode === 'DE' ? 30.0 : svStandard);

    const bruttoTotal = (inputs.base || 0) + (inputs.add || 0) + (inputs.bonus || 0);
    const divisor = (inputs.hours || 0) * wochensatz;
    
    const hourlyBase = (inputs.hours > 0) ? (inputs.base / divisor) : 0;
    const hourlyTotal = (inputs.hours > 0) ? (bruttoTotal / divisor) : 0;

    let svRate = svStandard;
    let statusLabel: 'OK' | 'PRÜFEN' | '---' = '---';
    let isMinWageIssue = false;
    let isMiniJobIssue = false;

    const isAssistenz = inputs.role === 'Assistenz' || inputs.role === 'Assistenzkraft';
    const isExempt = inputs.role === 'Azubi' || inputs.role === 'Prod. Azubi' || inputs.role === 'Unprod. Anteil';
    const isAzubiRole = !isAssistenz && (inputs.role === 'Azubi' || inputs.role === 'Prod. Azubi' || inputs.type === 'Azubi & Assistenz');

    if (landCode === 'IT') {
        const azubiF = Number(country?.ItBz_Faktor_Azubi) > 0 ? Number(country.ItBz_Faktor_Azubi) : 1.42;
        const tzF = Number(country?.ItBz_Faktor_Teilzeit) > 0 ? Number(country.ItBz_Faktor_Teilzeit) : 1.65;
        const vzF = Number(country?.ItBz_Faktor_Vollzeit) > 0 ? Number(country.ItBz_Faktor_Vollzeit) : 1.80;
        const fullTimeThreshold = Number(country?.ItBz_Vollzeit_Stundenschwelle) || 30;
        const mult = isAzubiRole ? azubiF : (Number(inputs.hours) || 0) < fullTimeThreshold ? tzF : vzF;
        svRate = Math.round((mult - 1) * 1000) / 10;
    } else if (landCode === 'AT') {
        const azubiPct = Number(country?.At_Lohnnebenkosten_Azubi) > 0 ? Number(country.At_Lohnnebenkosten_Azubi) : 35.0;
        const fachkraftPct = Number(country?.At_Lohnnebenkosten_Fachkraft) > 0 ? Number(country.At_Lohnnebenkosten_Fachkraft) : 52.0;
        svRate = isAzubiRole ? azubiPct : fachkraftPct;
    }

    if (inputs.hours > 0 && !isExempt && minWage > 0) {
        if (hourlyBase < (minWage - 0.01)) {
            statusLabel = 'PRÜFEN';
            isMinWageIssue = true;
        } else {
            statusLabel = 'OK';
        }
    } else if (inputs.hours > 0 && !minWage) {
         statusLabel = 'OK';
    }

    // Minijob-SV-Satz nur in DE — AT/CH/IT behalten Länder-Standard
    if (landCode === 'DE' && bruttoTotal > 0 && bruttoTotal <= miniJobLimit && !isExempt) {
        svRate = svMini;
    }

    return {
        bruttoTotal,
        hourlyBase,
        hourlyTotal,
        svRate,
        statusLabel,
        isMinWageIssue,
        isMiniJobIssue
    };
};

const computeExistingStaffDraftHasChanges = (
    employeeId: string,
    newInputs: any,
    clientData: any
): boolean => {
    const allOrigStaff = [
        ...(clientData.personalData || []).map((p: any) => ({ ...p, _sourceType: 'Produktiv (auch prod. Azubi)' })),
        ...(clientData.unproductivePersonalData || []).map((p: any) => ({ ...p, _sourceType: 'Unproduktiv' })),
        ...(clientData.azubiAssistenzData || []).map((p: any) => ({ ...p, _sourceType: 'Azubi & Assistenz' })),
    ];
    const original = allOrigStaff.find((p: any) => p && p.id === employeeId);

    if (!original && newInputs?.date !== '') return true;
    if (!original || newInputs?.date === '') return false;

    const isDiff = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) > 0.01;
    let cmpBase = original.grundgehalt || 0;
    let cmpHours = original.wochenstunden || 0;
    if ((original as any)._sourceType === 'Produktiv (auch prod. Azubi)') {
        const linkedUnprods = (clientData.unproductivePersonalData || []).filter((u: any) => {
            if (!u || String(u.taetigkeit || '').trim() !== 'Unprod. Anteil') return false;
            if (u.isSplitDeduction === false) return false;
            if (String(u.linkedProductiveEmployeeId || '') === String(employeeId)) return true;
            if (
                !u.linkedProductiveEmployeeId &&
                original.name &&
                String(u.name || '').trim() === String(original.name || '').trim()
            ) {
                return true;
            }
            return false;
        });
        for (const u of linkedUnprods) {
            cmpBase = Math.max(0, cmpBase - (Number(u.grundgehalt) || 0));
            cmpHours = Math.max(0, cmpHours - (Number(u.wochenstunden) || 0));
        }
    }

    if (isDiff(newInputs.base, cmpBase)) return true;
    if (isDiff(newInputs.add, original.lohnzusatz || 0)) return true;
    if (isDiff(newInputs.bonus, original.bonus || 0)) return true;
    if (isDiff(newInputs.hours, cmpHours)) return true;
    if (isDiff(newInputs.days, original.arbeitstage || 0)) return true;
    if (newInputs.type && newInputs.type !== (original as any)._sourceType) return true;
    if (newInputs.role && newInputs.role !== original.taetigkeit) return true;
    if (
        newInputs.linkedProductiveEmployeeId !== undefined &&
        String(newInputs.linkedProductiveEmployeeId || '') !== String((original as any).linkedProductiveEmployeeId || '')
    ) {
        return true;
    }
    if (
        newInputs.isSplitDeduction !== undefined &&
        Boolean(newInputs.isSplitDeduction) !== ((original as any).isSplitDeduction !== false)
    ) {
        return true;
    }
    if (newInputs.isNewUnprodAnteil) return true;
    if (newInputs.notes && String(newInputs.notes).trim() !== '') return true;
    return false;
};

const calculateEntrepreneurMetrics = (inputs: EntrepreneurDraftItem['inputs']) => {
    const totalBelastung = (inputs.entnahme || 0) + (inputs.social || 0);
    return { totalBelastung };
};

const calculateCostEffect = (inputs: CostDraftItem['inputs']) => {
    const { type, method, prevValue, newValue } = inputs;
    let effect = 0;
    if (type === 'new') {
        effect = newValue;
    } else if (type === 'flat') {
        if (method === 'diff') {
            effect = newValue;
        } else {
            effect = newValue - prevValue;
        }
    } else if (type === 'part') {
        effect = newValue - prevValue;
    }
    return effect;
};

export type CoreDataSliceType = Pick<AppState,
    | 'clientData' | 'team' | 'past' | 'future' | 'initializeStore' | 'setActiveBranch' | 'setClientData' 
    | 'updateNestedClientData' | 'addTableRow' | 'updateTableRow' | 'deleteTableRow' 
    | 'syncNavigation' | 'exportClientData' | 'importClientData' | 'updateBwaData' 
    | 'addPrognoseZeile' | 'updatePrognoseZeile' | 'removePrognoseZeile' 
    | 'setHj2Freigabe' | 'setHj1SperreGlobal' | 'resetFreischaltung' 
    | 'teamRegistry'
    | 'addTeamMember' | 'addToTeamRegistry' | 'authorizeTeamMember' | 'deauthorizeTeamMember'
    | 'updateTeamMember' | 'deleteTeamMember' 
    | 'updateGesamtleistungMonat' | 'updateGesamtleistungSetting' 
    | 'updateUrlaubsverlustMonat' | 'updateULBasisdaten' | 'updateULMonat' 
    | 'setCostAdjustmentRequired' | 'updateCachedUnternehmerMetrics' 
    | 'undo' | 'redo'
    | 'initStaffDrafts' | 'updateExistingStaffDraft' | 'seedExistingStaffDrafts' | 'commitDraftState' | 'resetStaffDraft'
    | 'addNewStaffDraft' | 'updateNewStaffDraft' | 'removeNewStaffDraft'
    | 'addEntrepreneurDraft' | 'updateEntrepreneurDraft' | 'removeEntrepreneurDraft'
    | 'addCostDraft' | 'updateCostDraft' | 'removeCostDraft'
    | 'addClientMasterDraft' | 'updateClientMasterDraft' | 'removeClientMasterDraft'
    | 'addOpeningHoursDraft' | 'updateOpeningHoursDraft' | 'removeOpeningHoursDraft'
    | 'setDraftLockStatus' | 'setPerformanceLock' 
> & {
    isDirty: boolean;
    cloudWriteLock: boolean;
    setIsDirty: (dirty: boolean) => void;
    freezePastMonths: (gueltigAbMonatIndex: number) => void;
};

/** Verhindert Doppel-Chronik bei Parallel-Klicks auf dieselbe Draft-Zeile */
const draftLockInflight = new Set<string>();

export const createCoreDataSlice: StateCreator<AppState, [], [], CoreDataSliceType> = (set, get) => ({
    clientData: defaultClientData,
    team: defaultTeam,
    teamRegistry: defaultTeamRegistry,
    past: [],
    future: [],
    
    isDirty: false,
    cloudWriteLock: false,
    setIsDirty: (dirty) => set({ isDirty: dirty }),

    freezePastMonths: (gueltigAbMonatIndex: number) => set((state) => {
        const currentClientData = state.clientData;
        const currentSettings = state.settings;
        
        const mwstSatz = resolveEffectiveClientMwstSatz(currentSettings, currentClientData);
        const activeMwstFaktor = 1 + (mwstSatz / 100);
        const ruecklagenZielFaktor = (currentSettings?.global?.Liquiditaet || 0) / 100;
        const gesamtUnternehmerlohn = (currentClientData.unternehmerData?.entnahme || 0) + (currentClientData.unternehmerData?.sozialkosten || 0);
        
        const newGesamtleistungData = (currentClientData.gesamtleistungData || []).map((monat: any, index: number) => {
            if (index < gueltigAbMonatIndex) {
                return {
                    ...monat,
                    abgeschlossen: true,
                    frozenCalculations: {
                        ...(monat.frozenCalculations || {}),
                        mwstFaktor: activeMwstFaktor,
                        ruecklagenZielFaktor: ruecklagenZielFaktor,
                        privatentnahme: gesamtUnternehmerlohn
                    }
                };
            }
            return monat;
        });

        const processHalbjahr = (halbjahrData: any[], startAbsoluteIndex: number) => {
            if (!Array.isArray(halbjahrData)) return [];
            return halbjahrData.map((monat: any, index: number) => {
                const absoluteIndex = startAbsoluteIndex + index;
                if (absoluteIndex < gueltigAbMonatIndex) {
                    return { 
                        ...monat, 
                        festmachen: true, 
                        lockedSnapshot: safeClone(monat) 
                    };
                }
                return monat;
            });
        };

        const newUlHalbjahr1 = processHalbjahr(currentClientData.ulHalbjahr1 || [], 0);
        const newUlHalbjahr2 = processHalbjahr(currentClientData.ulHalbjahr2 || [], 6);

        const newMitarbeiterLeistung = { ...(currentClientData.mitarbeiterLeistung || {}) };
        Object.keys(newMitarbeiterLeistung).forEach(empId => {
            const empData = newMitarbeiterLeistung[empId];
            if (empData) {
                newMitarbeiterLeistung[empId] = {
                    ...empData,
                    halbjahr1: processHalbjahr(empData.halbjahr1 || [], 0),
                    halbjahr2: processHalbjahr(empData.halbjahr2 || [], 6)
                };
            }
        });

        const assembledClientData = { 
            ...currentClientData, 
            gesamtleistungData: newGesamtleistungData,
            ulHalbjahr1: newUlHalbjahr1,
            ulHalbjahr2: newUlHalbjahr2,
            mitarbeiterLeistung: newMitarbeiterLeistung,
            frozenUntilMonth: gueltigAbMonatIndex 
        };
        
        let newClientData = safeClone(assembledClientData);
        const newSettings = syncParamsToGlobal(newClientData, state.settings);
        
        newClientData = injectSalonDefizit(newClientData, newSettings);

        saveToLocalStorage({ ...state, clientData: newClientData, settings: newSettings });
        return { clientData: newClientData, settings: newSettings, isDirty: true };
    }),

    initializeStore: async (targetUid?: string) => {
        // Vektor 2: Kontextwechsel — Timer killen, Epoch++, Store flashen, Gates sperren
        try {
            const { isolateSyncContext } = await import('../../config/firebaseSync');
            isolateSyncContext();
        } catch (_) {
            set({ isInitialized: false, cloudWriteLock: true, isDirty: false, past: [], future: [] });
            try {
                sessionStorage.setItem('PE_CLOUD_SYNC_BLOCKED', '1');
                sessionStorage.removeItem('PE_CLOUD_LOAD_OK');
            } catch (__) { /* ignore */ }
        }

        const finishWithData = (rawIn: any, extras: {
            settings?: any;
            team?: any[];
            teamRegistry?: any[];
            appSettings?: any;
            savedSnapshots?: any[];
            isRolledOver?: boolean;
        } = {}) => {
            let rawClientData = { ...defaultClientData, ...(rawIn || {}) };
            // God-Mode: isUnlockedByAdmin NICHT überschreiben — sonst öffnet die Modul-Firewall
            // alle Seiten für den Admin. Zugangstor ist PE_ADMIN_UNLOCKED / isShadowing.

            const yearsArr = Array.isArray(rawClientData.geschaeftsjahre) ? rawClientData.geschaeftsjahre : [];
            const latestYear = yearsArr.length > 0
                ? yearsArr.reduce((prev: any, current: any) => (Number(prev.jahr) > Number(current.jahr)) ? prev : current, yearsArr[0])
                : null;
            const isRolledOver = extras.isRolledOver ?? (latestYear?.status === 'abgeschlossen');

            try {
                // Status zum AKTIVEN Jahr (CURRENT_YEAR), nicht blind zum neuesten — verhindert Stale-Panzerglas
                const currentYearStr = localStorage.getItem('CURRENT_YEAR');
                const activeYearObj = currentYearStr
                    ? yearsArr.find((j: any) => String(j.jahr) === String(currentYearStr))
                    : latestYear;
                const statusForActive = (activeYearObj?.status && String(activeYearObj.status).trim())
                    ? String(activeYearObj.status).trim()
                    : 'datenerfassung';
                localStorage.setItem('CURRENT_YEAR_STATUS', statusForActive);
            } catch (_) { /* ignore */ }

            if (!isRolledOver) {
                // Draft-Locks (Änderungszentrale) NIEMALS beim Laden wipen —
                // Sperre bleibt bis zur expliziten Entsperr-Aktion bestehen.
                delete rawClientData.gesamtleistungLockedMetrics;
                if (rawClientData.unternehmerData) {
                    delete rawClientData.unternehmerData.lockedMetrics;
                }
            }

            rawClientData.isCostAdjustmentRequired = false;
            // KEIN "WIEDERHERGESTELLT"-Platzhalter — der wurde früher in die Cloud gesyncet
            // und hat echte Registrierungs-Nummern zerstört. Leer lassen, bis echte Nr. da ist.
            if (!rawClientData.mandantNr || rawClientData.mandantNr === 'WIEDERHERGESTELLT') {
                rawClientData.mandantNr = '';
            }
            rawClientData.firmenname = rawClientData.firmenname || "Bitte Namen eintragen";
            rawClientData.unternehmername = rawClientData.unternehmername || "";
            rawClientData.salonname = rawClientData.salonname || "";

            try {
                rawClientData = mergeLocalLeistungSnapshots(rawClientData);
                rawClientData = applyLocalFestsetzungBackup(rawClientData);
                rawClientData = applyPendingFestsetzungen(rawClientData);
                rawClientData = sanitizeClientData(rawClientData);
            } catch (sanitizeErr) {
                console.warn('initializeStore sanitize/merge Fehler — weiter mit Rohdaten:', sanitizeErr);
                rawClientData = { ...defaultClientData, ...rawClientData };
            }

            if (!sessionStorage.getItem('CURRENT_YEAR') && rawClientData.geschaeftsjahre && rawClientData.geschaeftsjahre.length > 0) {
                sessionStorage.setItem('CURRENT_YEAR', rawClientData.geschaeftsjahre[0].jahr.toString());
            }

            try {
                const restoredVorjahr = (rawClientData.gesamtleistungData || []).map((m: any) => ({
                    vorjahrWert1: m.vorjahrWert1 || 0,
                    vorjahrWert2: m.vorjahrWert2 || 0,
                    vorjahrProzent: m.vorjahrProzent || 0
                }));
                localStorage.setItem('pe_vorjahres_daten_v4', JSON.stringify(restoredVorjahr));
            } catch (_) { /* ignore */ }

            const baseSettings = mergeWithDefaultSettings(extras.settings || get().settings || {});
            let finalSettings = baseSettings;
            try {
                finalSettings = syncParamsToGlobal(rawClientData, baseSettings);
                rawClientData = injectSalonDefizit(rawClientData, finalSettings);
            } catch (settingsErr) {
                console.warn('initializeStore settings/defizit Fehler:', settingsErr);
            }

            // God-Mode: echtes isUnlockedByAdmin des Mandanten behalten (Sichtbarkeit = Mandanten-Sicht)
            // Zugang ohne Freigabe: PE_ADMIN_UNLOCKED / Impersonation in App.tsx

            // God-Mode: Team-Liste des Admins NIEMALS mit leerer/fremder Cloud-Liste überschreiben
            // (sonst fällt isTeam kurz auf false → Rücksprung ins Admin-Portal)
            const existingTeam = get().team || [];
            const existingRegistry = get().teamRegistry || [];
            const incomingTeam = extras.team;
            const isGodMode = !!sessionStorage.getItem('GOD_MODE_ID');
            const loadedTeam = (isGodMode && existingTeam.length > 0)
                ? existingTeam
                : (incomingTeam && incomingTeam.length > 0 ? incomingTeam : existingTeam);
            const loadedTeamRegistry = (isGodMode && existingRegistry.length > 0)
                ? existingRegistry
                : migrateTeamRegistry(loadedTeam, extras.teamRegistry || existingRegistry);

            set({
                clientData: rawClientData,
                settings: finalSettings,
                team: loadedTeam,
                teamRegistry: loadedTeamRegistry,
                appSettings: extras.appSettings || get().appSettings || {},
                savedSnapshots: extras.savedSnapshots || [],
                unlockedPremiumTools: Array.isArray(rawClientData.unlockedPremiumTools)
                    ? rawClientData.unlockedPremiumTools.filter((id: unknown) => typeof id === 'string')
                    : [],
                isInitialized: true,
                cloudWriteLock: false,
                isDirty: false,
                past: [],
                future: [],
                rolloverConfirm: !!isRolledOver
            });
            // WICHTIG: PE_CLOUD_SYNC_BLOCKED wird hier NICHT entfernt — nur ein
            // erfolgreicher Cloud-Load (oder reiner Lokal-Modus ohne targetUid) gibt den Sync frei.

            try {
                const { team, teamRegistry, appSettings, savedSnapshots } = get();
                saveToLocalStorage({ clientData: rawClientData, settings: finalSettings, team, teamRegistry, appSettings, savedSnapshots });
            } catch (_) { /* ignore */ }

            // PE-Profit-Archiv (inkl. bankerPitchText) aus clientData in den Workspace-State heben
            try {
                get().hydratePeProfitFromClientData?.();
                get().hydratePlanungsAnalyseFromClientData?.();
            } catch (_) { /* ignore */ }

            // Freigeschaltet ohne Datum → Laufzeitdaten nachziehen (ohne Neu-Buchung)
            void (async () => {
                try {
                    const { repairMissingPremiumActivationDates } = await import(
                        '../../utils/premiumCostBridge'
                    );
                    await repairMissingPremiumActivationDates();
                } catch (_) { /* ignore */ }
            })();
        };

        if (targetUid) {
            const isGodModeLoad = (() => {
                try { return !!sessionStorage.getItem('GOD_MODE_ID'); } catch { return false; }
            })();
            try {
                // Gates bereits durch isolateSyncContext gesetzt — Lock bleibt bis finishWithData
                set({ isInitialized: false, cloudWriteLock: true });

                const { loadCloudStateToLocal, waitForCloudWriteLock } = await import('../../config/firebaseSync');

                // Kurz warten, aber niemals ewig — God-Mode darf nicht am Lock hängen
                await Promise.race([
                    waitForCloudWriteLock(2000),
                    new Promise<void>((resolve) => setTimeout(resolve, 2000)),
                ]);
                // Lock bleibt true bis finishWithData — nicht vorzeitig freigeben

                const abrufJahr = sessionStorage.getItem('CURRENT_YEAR')
                    || localStorage.getItem('CURRENT_YEAR')
                    || new Date().getFullYear().toString();

                const cloudData = await Promise.race([
                    loadCloudStateToLocal(targetUid, abrufJahr),
                    new Promise<null>((resolve) => setTimeout(() => {
                        console.error('initializeStore: Cloud-Load Timeout (8s)');
                        resolve(null);
                    }, 8000)),
                ]);

                // Vektor 3: LOAD_OK nur nach Server-Verifikation
                if (cloudData?.clientData && cloudData?.serverVerified === true) {
                    try {
                        sessionStorage.setItem('PE_CLOUD_LOAD_OK', '1');
                        sessionStorage.removeItem('PE_CLOUD_SYNC_BLOCKED');
                    } catch (_) { /* ignore */ }
                    finishWithData(cloudData.clientData, {
                        settings: cloudData.settings,
                        team: cloudData.team,
                        teamRegistry: cloudData.teamRegistry,
                        appSettings: cloudData.appSettings,
                        savedSnapshots: cloudData.savedSnapshots,
                    });
                    return;
                }

                // God-Mode: KEIN localStorage-/Default-Fallback → sonst leere Akte in Cloud
                if (isGodModeLoad) {
                    console.error('initializeStore: God-Mode Cloud-Load fehlgeschlagen — Sync bleibt gesperrt');
                    try { sessionStorage.removeItem('PE_CLOUD_LOAD_OK'); } catch (_) { /* ignore */ }
                    set({
                        isInitialized: true,
                        cloudWriteLock: true,
                        clientData: {
                            ...defaultClientData,
                            firmenname: 'Laden fehlgeschlagen',
                        },
                    });
                    return;
                }
            } catch (e) {
                console.error("Firebase Sync Fehler:", e);
                try { sessionStorage.removeItem('PE_CLOUD_LOAD_OK'); } catch (_) { /* ignore */ }
                if (isGodModeLoad) {
                    set({ isInitialized: true, cloudWriteLock: true });
                    return;
                }
            }
        }

        const loadedState = loadFromLocalStorage();
        // Sync nur freigeben, wenn KEIN Cloud-Load versucht wurde (reiner Lokal-Modus).
        // Nach fehlgeschlagenem Cloud-Load bleibt der Sync gesperrt: lokale Altdaten
        // dürfen NIEMALS ungeprüft eine Cloud-Akte überschreiben (Feldstraße-Fall).
        if (!targetUid) {
            try {
                sessionStorage.setItem('PE_CLOUD_LOAD_OK', '1');
                sessionStorage.removeItem('PE_CLOUD_SYNC_BLOCKED');
            } catch (_) { /* ignore */ }
        } else {
            console.warn('initializeStore: Cloud-Load fehlgeschlagen — Autosync bleibt gesperrt (nur lokale Anzeige)');
            try { sessionStorage.removeItem('PE_CLOUD_LOAD_OK'); } catch (_) { /* ignore */ }
        }
        if (loadedState?.clientData) {
            finishWithData(loadedState.clientData, {
                settings: loadedState.settings,
                team: loadedState.team,
                teamRegistry: loadedState.teamRegistry,
                appSettings: loadedState.appSettings,
                savedSnapshots: loadedState.savedSnapshots,
            });
        } else {
            finishWithData({ ...defaultClientData });
        }
    },

    setActiveBranch: async (branchId: string) => {
        const id = String(branchId || '').trim();
        if (!id) return;

        // Vektor 2: Filialwechsel = Kontextwechsel
        try {
            const { isolateSyncContext } = await import('../../config/firebaseSync');
            isolateSyncContext();
        } catch (_) { /* ignore */ }

        try {
            sessionStorage.setItem('CURRENT_BRANCH', id);
            localStorage.setItem('CURRENT_BRANCH', id);
        } catch (_) { /* ignore */ }

        set((state) => ({
            isInitialized: false,
            cloudWriteLock: true,
            clientData: {
                ...defaultClientData,
                ...(state.clientData || {}),
                activeBranchId: id,
                branchName: (state.clientData?.branches || []).find((b: any) => b.id === id)?.name
                    || state.clientData?.branchName
                    || '',
                branches: state.clientData?.branches || [],
                hasBranches: state.clientData?.hasBranches,
            },
            past: [],
            future: [],
            isDirty: false,
        }));

        try {
            const { auth } = await import('../../config/firebase');
            const useAdminStore = (await import('../adminStore')).default;
            const { doc, setDoc } = await import('firebase/firestore');
            const { db } = await import('../../config/firebase');
            const uid = useAdminStore.getState().impersonatedClientId || auth.currentUser?.uid;
            if (uid) {
                await setDoc(doc(db, 'kalkulation_mandanten', uid), {
                    activeBranchId: id,
                    'clientData.activeBranchId': id,
                }, { merge: true });
                await get().initializeStore(uid);
            }
        } catch (e) {
            console.error('setActiveBranch failed:', e);
        }
    },

    setClientData: (updater) => {
        const { settings } = get();
        const newPast = getNewPast(get());
        let newClientData = typeof updater === 'function' ? (updater as any)(get().clientData) : updater;
        
        const newSettings = syncParamsToGlobal(newClientData, settings);
        
        newClientData = injectSalonDefizit(newClientData, newSettings);

        set({ past: newPast, future: [], clientData: newClientData, settings: newSettings, isDirty: true }); 
        const { team, appSettings, savedSnapshots } = get();
        saveToLocalStorage({ clientData: newClientData, settings: newSettings, team, appSettings, savedSnapshots });
    },

    updateNestedClientData: (parentKey, nestedKey, value) => set((state) => {
        // Lohnfaktor HJ1-Felder: nach HJ2-Freigabe hart gesperrt (auch Gott-Modus)
        if (
            parentKey === 'lohnfaktorData' &&
            isHj1WriteBlocked(state.clientData) &&
            (nestedKey === 'mindestumsatz1' || nestedKey === 'kostendeckungsfaktor1')
        ) {
            return state;
        }
        const newPast = getNewPast(state);
        // 💉 Fallback, falls das Parent-Objekt komplett fehlt
        const parentData = (state.clientData as any)[parentKey] || {};
        if (typeof parentData === 'object' && parentData !== null) {
            const newParentData = { ...parentData, [nestedKey]: value };
            let newClientData = { ...state.clientData, [parentKey]: newParentData };
            
            const newSettings = syncParamsToGlobal(newClientData, state.settings);
            newClientData = injectSalonDefizit(newClientData, newSettings);

            saveToLocalStorage({ clientData: newClientData, settings: newSettings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots });
            return { clientData: newClientData, settings: newSettings, past: newPast, future: [], isDirty: true };
        }
        return state;
    }),

    addTableRow: (key, template) => set(state => {
        const newPast = getNewPast(state);
        const tableData = (state.clientData as any)[key];
        if (!Array.isArray(tableData)) return state;
        const newRow = { ...template, id: generateSimpleId() };
        const newTableData = [...tableData, newRow];
        const newClientData = { ...state.clientData, [key]: newTableData };
        saveToLocalStorage({ clientData: newClientData, settings: state.settings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true };
    }),

    updateTableRow: (key, index, field, value) => set(state => {
        markStaffTableEdit(key);
        const newPast = getNewPast(state);
        const tableData = (state.clientData as any)[key];
        if (!Array.isArray(tableData) || !tableData[index]) return state;
        const newTableData = [...tableData];
        const patchObj =
            field && typeof field === 'object'
                ? (field as Record<string, any>)
                : { [field]: value };
        const updatedRow: any = { ...newTableData[index], ...patchObj };
        if (
            key === 'unproductivePersonalData' &&
            'taetigkeit' in patchObj &&
            patchObj.taetigkeit !== 'Unprod. Anteil'
        ) {
            delete updatedRow.linkedProductiveEmployeeId;
            updatedRow.isSplitDeduction = false;
        }
        newTableData[index] = updatedRow;
        const newClientData: any = { ...state.clientData, [key]: newTableData };
        if (
            key === 'personalData' &&
            'name' in patchObj &&
            updatedRow.id &&
            Array.isArray(state.clientData.unproductivePersonalData)
        ) {
            newClientData.unproductivePersonalData = state.clientData.unproductivePersonalData.map((u: any) =>
                u && u.taetigkeit === 'Unprod. Anteil' && u.linkedProductiveEmployeeId === updatedRow.id
                    ? { ...u, name: patchObj.name }
                    : u
            );
        }
        saveToLocalStorage({ clientData: newClientData, settings: state.settings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true };
    }),

    deleteTableRow: (key, index) => set(state => {
        markStaffTableEdit(key);
        const newPast = getNewPast(state);
        const tableData = (state.clientData as any)[key];
        if (!Array.isArray(tableData) || !tableData[index]) return state;
        const deletedRow = tableData[index];
        const newTableData = tableData.filter((_, i) => i !== index);
        const newClientData: any = { ...state.clientData, [key]: newTableData };
        if (key === 'personalData' && deletedRow?.id && Array.isArray(state.clientData.unproductivePersonalData)) {
            newClientData.unproductivePersonalData = state.clientData.unproductivePersonalData.map((u: any) =>
                u && u.linkedProductiveEmployeeId === deletedRow.id
                    ? { ...u, linkedProductiveEmployeeId: undefined, isSplitDeduction: false }
                    : u
            );
        }
        saveToLocalStorage({ clientData: newClientData, settings: state.settings, team: state.team, appSettings: state.appSettings, savedSnapshots: state.savedSnapshots });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true };
    }),

    syncNavigation: () => {},

    exportClientData: () => { 
        try { 
            const { clientData, settings, team, appSettings, savedSnapshots } = get(); 
            const settingsToExport = { ...settings }; 
            delete (settingsToExport as any).country; 
            const dataToExport: StoredState = { clientData, settings: settingsToExport, team, appSettings, savedSnapshots }; 
            const jsonString = JSON.stringify(dataToExport, null, 2); 
            const blob = new Blob([jsonString], { type: 'application/json' }); 
            const url = URL.createObjectURL(blob); 
            const link = document.createElement('a'); 
            link.href = url; 
            link.download = `PreisExpert_Backup_${new Date().toISOString().split('T')[0]}.json`; 
            document.body.appendChild(link); 
            link.click(); 
            document.body.removeChild(link); 
            URL.revokeObjectURL(url); 
        } catch (e) { console.error("Export fehlgeschlagen:", e); } 
    },
    
    importClientData: (file: File) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const result = event.target?.result;
                if (typeof result !== 'string') throw new Error('Datei fehlerhaft.');
                const { clientData, settings, team, appSettings, savedSnapshots } = JSON.parse(result) as StoredState;
                let loadedClientData = { ...defaultClientData, ...clientData };
                loadedClientData.unternehmerData = {...defaultClientData.unternehmerData, ...clientData.unternehmerData};
                loadedClientData.bwaData = {...defaultClientData.bwaData, ...clientData.bwaData};
                
                loadedClientData = sanitizeClientData(loadedClientData);

                const limitArray = (arr: any[]) => arr.map(m => {
                    if (m.bedienumsatzBrutto > 100000 || m.vkUmsatzBrutto > 100000) {
                        return { ...m, bedienumsatzBrutto: 0, vkUmsatzBrutto: 0 };
                    }
                    return m;
                });
                loadedClientData.ulHalbjahr1 = limitArray(loadedClientData.ulHalbjahr1);
                loadedClientData.ulHalbjahr2 = limitArray(loadedClientData.ulHalbjahr2);
                
                const loadedSettings = { ...defaultSettings, ...settings };
                const activeLand = String(loadedClientData.land || loadedClientData.Land || 'DE').toUpperCase() as "DE" | "CH" | "AT" | "IT";
                
                const base = loadedSettings.countries?.[activeLand] || loadedSettings.countries?.DE || {};
                const currencyCode = base.currency || (activeLand === 'CH' ? 'CHF' : 'EUR');
                const mwst = base.MwSt_Satz ?? (activeLand === 'CH' ? 8.1 : 19);
                loadedSettings.country = { ...base, currency: currencyCode, MwSt_Satz: mwst };
                loadedSettings.selectedCountryId = activeLand; 

                const finalSettings = syncParamsToGlobal(loadedClientData, loadedSettings);
                
                loadedClientData = injectSalonDefizit(loadedClientData, finalSettings);

                const fullState = { clientData: loadedClientData, settings: finalSettings, team: team || defaultTeam, appSettings: appSettings || defaultAppSettings, savedSnapshots: savedSnapshots || [], past: [], future: [], isDirty: true };
                
                set(fullState as any);
                saveToLocalStorage(fullState);
                resolve('Daten importiert & bereinigt!');
            } catch (e) { reject('Fehler beim Import.'); }
        };
        reader.readAsText(file);
    }),

    updateBwaData: (key, value) => set(state => { 
        const newPast = getNewPast(state);
        const newClientData = { ...state.clientData, bwaData: { ...state.clientData.bwaData, [key]: value } }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    addPrognoseZeile: (defaults) => set(state => { 
        const newPast = getNewPast(state);
        const bwaData = state.clientData.bwaData || {}; 
        const prognoseZeilen = bwaData.prognoseZeilen || []; 
        const initialLohn = defaults?.lohn ?? bwaData.prognoseBruttolohn ?? 0; 
        const initialFaktor = defaults?.faktor ?? bwaData.prognoseFaktor ?? 2.5; 
        const mwstSatz = resolveEffectiveClientMwstSatz(state.settings, state.clientData); 
        const initialJahresumsatz = ((initialLohn * initialFaktor) * 10) / (1 + mwstSatz / 100); 
        const neueZeile: PrognoseZeile = { id: generateSimpleId(), mitarbeiterId: '', lohn: initialLohn, faktor: initialFaktor, jahresumsatz: initialJahresumsatz, datum: new Date().toISOString().split('T')[0], aktiv: false }; 
        const newClientData = { ...state.clientData, bwaData: { ...bwaData, prognoseZeilen: [...prognoseZeilen, neueZeile] } }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    updatePrognoseZeile: (index, field, value) => set(state => { 
        const newPast = getNewPast(state);
        const prognoseZeilen = [...(state.clientData.bwaData.prognoseZeilen || [])]; 
        if (!prognoseZeilen[index]) return state; 
        prognoseZeilen[index] = { ...prognoseZeilen[index], [field]: value };
        if (field === 'lohn' || field === 'faktor') { 
            const zeile = prognoseZeilen[index]; 
            const mwstSatz = resolveEffectiveClientMwstSatz(state.settings, state.clientData); 
            const jahresBrutto = zeile.lohn * zeile.faktor;
            zeile.jahresumsatz = (jahresBrutto * 10) / (1 + mwstSatz / 100); 
        } 
        const newClientData = { ...state.clientData, bwaData: { ...state.clientData.bwaData, prognoseZeilen } }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    removePrognoseZeile: (id) => set(state => { 
        const newPast = getNewPast(state);
        const prognoseZeilen = (state.clientData.bwaData.prognoseZeilen || []).filter((z: PrognoseZeile) => z.id !== id); 
        const newClientData = { ...state.clientData, bwaData: { ...state.clientData.bwaData, prognoseZeilen } }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    setHj2Freigabe: (isFreigegeben, lockContext) => set((state) => { 
        const newPast = getNewPast(state);
        let newClientData: any = { ...state.clientData, isHJ2Freigegeben: isFreigegeben };

        // HJ2 freischalten = HJ1 automatisch sperren + Stammdaten einfrieren
        if (isFreigegeben) {
            newClientData.isHJ1GesperrtGlobal = true;

            const { effectivePersonalData: personalData } = applyUnproductiveSplitToPersonalLists(
                state.clientData?.personalData,
                state.clientData?.unproductivePersonalData
            );
            const leistungsprovision = (state.clientData as any)?.leistungsprovision || {};
            const lohnfaktorAktiv = Number(lockContext?.lohnfaktorAktiv);
            const kostendeckungsfaktor1 = Number(state.clientData.lohnfaktorData?.kostendeckungsfaktor1) || 2.8;
            const mitarbeiterLeistung = { ...(state.clientData.mitarbeiterLeistung || {}) };

            personalData.forEach((employee: any) => {
                if (!employee?.id) return;

                const existingLeistung = mitarbeiterLeistung[employee.id] || {};
                const selectedDeviceIds = Array.isArray(existingLeistung.selectedDeviceIds)
                    ? [...existingLeistung.selectedDeviceIds]
                    : [];

                mitarbeiterLeistung[employee.id] = {
                    ...existingLeistung,
                    frozenStammdatenHJ1: {
                        name: employee.name || '',
                        wochenstunden: Number(employee.wochenstunden) || 0,
                        arbeitstage: Number(employee.arbeitstage) || 5,
                        grundgehalt: Number(employee.grundgehalt) || 0,
                        lohnzusatz: Number(employee.lohnkostenzusatz ?? employee.lohnzusatz) || 0,
                        bonus: Number(employee.bonus) || 0,
                        taetigkeit: employee.taetigkeit || '',
                        zugehoerigkeit: employee.zugehoerigkeit || '',
                        arbeitetMitGeraeten: Boolean(employee.arbeitetMitGeraeten ?? employee.hasGeraete),
                        selectedDeviceIds,
                        lohnfaktorAktiv: Number.isFinite(lohnfaktorAktiv) && lohnfaktorAktiv > 0 ? lohnfaktorAktiv : undefined,
                        kostendeckungsfaktor1,
                        provisionErstesHalbjahr: leistungsprovision.erstesHalbjahr
                            ? JSON.parse(JSON.stringify(leistungsprovision.erstesHalbjahr))
                            : { provisionDienstleistung: 10, provisionVerkauf: 10 },
                    },
                };
            });

            newClientData = { ...newClientData, mitarbeiterLeistung };

            if (lockContext?.unternehmerFreeze) {
                newClientData.frozenUnternehmerStammdatenHJ1 = JSON.parse(JSON.stringify(lockContext.unternehmerFreeze));
                try {
                    newClientData.frozenUnternehmerStammdatenHJ1_string = JSON.stringify(newClientData.frozenUnternehmerStammdatenHJ1);
                } catch {
                    delete newClientData.frozenUnternehmerStammdatenHJ1_string;
                }
            }

            syncLeistungSnapshotStringsInClientData(newClientData);
        } else {
            // Freigabe zurück = HJ1-Sperre mit aufheben (sonst steckt die Akte fest)
            newClientData.isHJ1GesperrtGlobal = false;
            delete newClientData.frozenUnternehmerStammdatenHJ1;
            delete newClientData.frozenUnternehmerStammdatenHJ1_string;
            const mitarbeiterLeistung = { ...(state.clientData.mitarbeiterLeistung || {}) };
            Object.keys(mitarbeiterLeistung).forEach((empId) => {
                const existing = mitarbeiterLeistung[empId];
                if (!existing) return;
                const cleaned = { ...existing };
                delete cleaned.frozenStammdatenHJ1;
                delete cleaned.frozenStammdatenHJ1_string;
                mitarbeiterLeistung[empId] = cleaned;
            });
            newClientData = { ...newClientData, mitarbeiterLeistung };
            syncLeistungSnapshotStringsInClientData(newClientData);
        }

        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    setHj1SperreGlobal: (isGesperrt, lockContext) => set((state) => { 
        const newPast = getNewPast(state);
        let newClientData: any = { ...state.clientData, isHJ1GesperrtGlobal: isGesperrt };

        if (isGesperrt) {
            const { effectivePersonalData: personalData } = applyUnproductiveSplitToPersonalLists(
                state.clientData?.personalData,
                state.clientData?.unproductivePersonalData
            );
            const leistungsprovision = (state.clientData as any)?.leistungsprovision || {};
            const lohnfaktorAktiv = Number(lockContext?.lohnfaktorAktiv);
            const kostendeckungsfaktor1 = Number(state.clientData.lohnfaktorData?.kostendeckungsfaktor1) || 2.8;
            const mitarbeiterLeistung = { ...(state.clientData.mitarbeiterLeistung || {}) };

            // Immer überschreiben: Snapshot = Werte zum Sperrzeitpunkt (nicht Early-Freeze behalten)
            personalData.forEach((employee: any) => {
                if (!employee?.id) return;

                const existingLeistung = mitarbeiterLeistung[employee.id] || {};
                const selectedDeviceIds = Array.isArray(existingLeistung.selectedDeviceIds)
                    ? [...existingLeistung.selectedDeviceIds]
                    : [];

                mitarbeiterLeistung[employee.id] = {
                    ...existingLeistung,
                    frozenStammdatenHJ1: {
                        name: employee.name || '',
                        wochenstunden: Number(employee.wochenstunden) || 0,
                        arbeitstage: Number(employee.arbeitstage) || 5,
                        grundgehalt: Number(employee.grundgehalt) || 0,
                        lohnzusatz: Number(employee.lohnkostenzusatz ?? employee.lohnzusatz) || 0,
                        bonus: Number(employee.bonus) || 0,
                        taetigkeit: employee.taetigkeit || '',
                        zugehoerigkeit: employee.zugehoerigkeit || '',
                        arbeitetMitGeraeten: Boolean(employee.arbeitetMitGeraeten ?? employee.hasGeraete),
                        selectedDeviceIds,
                        lohnfaktorAktiv: Number.isFinite(lohnfaktorAktiv) && lohnfaktorAktiv > 0 ? lohnfaktorAktiv : undefined,
                        kostendeckungsfaktor1,
                        provisionErstesHalbjahr: leistungsprovision.erstesHalbjahr
                            ? JSON.parse(JSON.stringify(leistungsprovision.erstesHalbjahr))
                            : { provisionDienstleistung: 10, provisionVerkauf: 10 },
                    },
                };
            });

            newClientData = { ...newClientData, mitarbeiterLeistung };

            if (lockContext?.unternehmerFreeze) {
                newClientData.frozenUnternehmerStammdatenHJ1 = JSON.parse(JSON.stringify(lockContext.unternehmerFreeze));
                try {
                    newClientData.frozenUnternehmerStammdatenHJ1_string = JSON.stringify(newClientData.frozenUnternehmerStammdatenHJ1);
                } catch {
                    delete newClientData.frozenUnternehmerStammdatenHJ1_string;
                }
            }

            syncLeistungSnapshotStringsInClientData(newClientData);
        }

        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    setCostAdjustmentRequired: (isRequired) => set(state => { 
        const newPast = getNewPast(state);
        const newClientData = { ...state.clientData, isCostAdjustmentRequired: isRequired }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    resetFreischaltung: () => set(state => { 
        const newPast = getNewPast(state);
        const mitarbeiterLeistung = { ...(state.clientData.mitarbeiterLeistung || {}) };

        // Stammdaten-Freeze aufheben — sonst bleiben HJ1-Werte nach Reset tot
        Object.keys(mitarbeiterLeistung).forEach((empId) => {
            const existing = mitarbeiterLeistung[empId];
            if (!existing) return;
            const cleaned = { ...existing };
            delete cleaned.frozenStammdatenHJ1;
            delete cleaned.frozenStammdatenHJ1_string;
            mitarbeiterLeistung[empId] = cleaned;
        });

        const newClientData: any = {
            ...state.clientData,
            isHJ2Freigegeben: false,
            isHJ1GesperrtGlobal: false,
            isCostAdjustmentRequired: false,
            mitarbeiterLeistung,
        };
        delete newClientData.frozenUnternehmerStammdatenHJ1;
        delete newClientData.frozenUnternehmerStammdatenHJ1_string;

        syncLeistungSnapshotStringsInClientData(newClientData);
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    addToTeamRegistry: (member) => set(state => { 
        const newPast = getNewPast(state);
        const newMember = { ...member, id: generateTeamMemberId() };
        const newRegistry = [...(state.teamRegistry || []), newMember]; 
        saveToLocalStorage({ ...state, teamRegistry: newRegistry }); 
        return { teamRegistry: newRegistry, past: newPast, future: [], isDirty: true }; 
    }),

    addTeamMember: (member) => {
        get().addToTeamRegistry(member);
    },

    authorizeTeamMember: (id) => set(state => {
        const member = (state.teamRegistry || []).find(m => m.id === id);
        if (!member || state.team.some(m => m.id === id)) return state;
        const newPast = getNewPast(state);
        const newTeam = [...state.team, member];
        saveToLocalStorage({ ...state, team: newTeam });
        return { team: newTeam, past: newPast, future: [], isDirty: true };
    }),

    deauthorizeTeamMember: (id) => set(state => {
        if (!state.team.some(m => m.id === id)) return state;
        const newPast = getNewPast(state);
        const newTeam = state.team.filter(m => m.id !== id);
        saveToLocalStorage({ ...state, team: newTeam });
        return { team: newTeam, past: newPast, future: [], isDirty: true };
    }),

    updateTeamMember: (updatedMember) => set(state => { 
        const newPast = getNewPast(state);
        const newRegistry = (state.teamRegistry || []).map(m => m.id === updatedMember.id ? updatedMember : m); 
        const newTeam = state.team.map(m => m.id === updatedMember.id ? updatedMember : m); 
        saveToLocalStorage({ ...state, team: newTeam, teamRegistry: newRegistry }); 
        return { team: newTeam, teamRegistry: newRegistry, past: newPast, future: [], isDirty: true }; 
    }),

    deleteTeamMember: (id) => set(state => { 
        const newPast = getNewPast(state);
        const newRegistry = (state.teamRegistry || []).filter(m => m.id !== id); 
        const newTeam = state.team.filter(m => m.id !== id); 
        saveToLocalStorage({ ...state, team: newTeam, teamRegistry: newRegistry }); 
        return { team: newTeam, teamRegistry: newRegistry, past: newPast, future: [], isDirty: true }; 
    }),

    // 💉 CHIRURGIE-FIX: LÜCKENLOSE ARRAY-ERSTELLUNG (VERHINDERT SILENT FAIL)
    updateGesamtleistungMonat: (index, field, value) => set(state => { 
        // HJ1-Monate (0–5) hart gesperrt nach HJ2-Freigabe (auch Gott-Modus)
        if (index < 6 && isHj1WriteBlocked(state.clientData)) {
            return state;
        }

        const newPast = getNewPast(state);
        const newGesamtleistungData = [...(state.clientData.gesamtleistungData || [])]; 
        
        if (!newGesamtleistungData[index]) {
            const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
            for (let i = 0; i <= index; i++) {
                if (!newGesamtleistungData[i]) {
                    newGesamtleistungData[i] = { 
                        monat: monthNames[i] || `Monat ${i+1}`, 
                        bedienumsatzBrutto: 0, 
                        vkUmsatzBrutto: 0, 
                        aussergewoehnlich: 0, 
                        kostenErhoeht: false, 
                        abgeschlossen: false,
                        frozenCalculations: null
                    };
                }
            }
        }

        newGesamtleistungData[index] = { ...newGesamtleistungData[index], [field]: value };
        let newClientData = { ...state.clientData, gesamtleistungData: newGesamtleistungData }; 
        
        newClientData = injectSalonDefizit(newClientData, state.settings);

        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    updateGesamtleistungSetting: (field, value) => set(state => { 
        const newPast = getNewPast(state);
        const currentSettings = state.clientData.gesamtleistungSettings || { planStundenTag: 8 };
        const newSettings = { ...currentSettings, [field]: Number(value) || 0 }; 
        const newClientData = { ...state.clientData, gesamtleistungSettings: newSettings }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    // 💉 CHIRURGIE-FIX: LÜCKENLOSE ARRAY-ERSTELLUNG (VERHINDERT SILENT FAIL)
    updateUrlaubsverlustMonat: (index, field, value) => set(state => { 
        const newPast = getNewPast(state);
        const newUrlaubsverlustData = [...(state.clientData.urlaubsverlustData || [])]; 
        
        if (!newUrlaubsverlustData[index]) {
            const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
            for (let i = 0; i <= index; i++) {
                if (!newUrlaubsverlustData[i]) {
                    newUrlaubsverlustData[i] = { 
                        monat: monthNames[i] || `Monat ${i+1}`, 
                        urlaub: 0, 
                        krankheit: 0 
                    };
                }
            }
        }

        newUrlaubsverlustData[index] = { ...newUrlaubsverlustData[index], [field]: Number(value) || 0 }; 
        const newClientData = { ...state.clientData, urlaubsverlustData: newUrlaubsverlustData }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    updateULBasisdaten: (field, value) => set(state => { 
        const newPast = getNewPast(state);
        const currentBasis = state.clientData.ulBasisdaten || { simulationsStundenTag: 5.0 };
        const newBasisdaten = { ...currentBasis, [field]: Number(value) || 0 }; 
        const newClientData = { ...state.clientData, ulBasisdaten: newBasisdaten }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    // 💉 CHIRURGIE-FIX: LÜCKENLOSE ARRAY-ERSTELLUNG (VERHINDERT SILENT FAIL)
    updateULMonat: (halbjahr, index, field, value) => set(state => {
        // Mandant: HJ1 gesperrt. God-Mode darf korrigieren. Nie HJ1→HJ2 spiegeln.
        if (halbjahr === 'ulHalbjahr1' && isHj1WriteBlocked(state.clientData)) {
            return state;
        }

        const newPast = getNewPast(state);
        const currentHalbjahr = (state.clientData as any)[halbjahr] || [];
        const newHalbjahr = [...currentHalbjahr];
        
        if (!newHalbjahr[index]) {
            const mNames = halbjahr === 'ulHalbjahr1' 
                ? ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni'] 
                : ['Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
            for (let i = 0; i <= index; i++) {
                if (!newHalbjahr[i]) {
                    newHalbjahr[i] = { 
                        monat: mNames[i] || `Monat ${i+1}`, 
                        bedienumsatzBrutto: 0, 
                        vkUmsatzBrutto: 0, 
                        urlaubsTage: 0, 
                        krankheitsTage: 0, 
                        festmachen: false 
                    };
                }
            }
        }

        let sanitizedValue: any;
        if (field === 'festmachen' || field === 'lockedSnapshot') {
            sanitizedValue = value;
        } else {
            sanitizedValue = forceNumber(value);
        }

        newHalbjahr[index] = { ...newHalbjahr[index], [field as keyof ULMonatsDaten]: sanitizedValue as never };
        let newClientData = { ...state.clientData, [halbjahr]: newHalbjahr };
        if (halbjahr === 'ulHalbjahr1') {
            newClientData = syncUnternehmerSnapshotStrings(newClientData);
        }
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, past: newPast, future: [], isDirty: true }; 
    }),

    updateCachedUnternehmerMetrics: (metrics) => set(state => { 
        const newClientData = { ...state.clientData, cachedUnternehmerMetrics: metrics }; 
        saveToLocalStorage({ ...state, clientData: newClientData }); 
        return { clientData: newClientData, isDirty: true }; 
    }),

    undo: () => set(state => {
        if (!(state as any).isInitialized || state.past.length === 0) return state;
        
        const previous = state.past[state.past.length - 1];
        if (!previous || !previous.clientData) return state;

        const newPast = state.past.slice(0, -1);
        const newFuture = [{ clientData: safeClone(state.clientData), settings: safeClone(state.settings) }, ...state.future];
        
        const safeClientData = sanitizeClientData(previous.clientData);
        const safeSettings = syncParamsToGlobal(safeClientData, previous.settings || state.settings || {}); 
        
        const finalClientData = injectSalonDefizit(safeClientData, safeSettings);

        saveToLocalStorage({ ...state, clientData: finalClientData, settings: safeSettings });
        return { past: newPast, future: newFuture, clientData: finalClientData, settings: safeSettings, isDirty: true };
    }),

    redo: () => set(state => {
        if (!(state as any).isInitialized || state.future.length === 0) return state;
        
        const next = state.future[0];
        if (!next || !next.clientData) return state;

        const newFuture = state.future.slice(1);
        const newPast = getNewPast(state);
        
        const safeClientData = sanitizeClientData(next.clientData);
        const safeSettings = syncParamsToGlobal(safeClientData, next.settings || state.settings || {});
        
        const finalClientData = injectSalonDefizit(safeClientData, safeSettings);

        saveToLocalStorage({ ...state, clientData: finalClientData, settings: safeSettings });
        return { past: newPast, future: newFuture, clientData: finalClientData, settings: safeSettings, isDirty: true };
    }),

    initStaffDrafts: () => set(state => {
        const currentDrafts = state.clientData.drafts || { existingStaff: {}, newStaff: [], entrepreneur: [], costs: [], clientMaster: [], openingHours: [] };
        const newClientData = { ...state.clientData, drafts: currentDrafts };
        return { clientData: newClientData, isDirty: true };
    }),

    updateExistingStaffDraft: (employeeId, field, value) => set(state => {
        const { clientData, settings } = state;
        
        const drafts = { 
            ...clientData.drafts,
            existingStaff: { ...(clientData.drafts?.existingStaff || {}) } 
        };

        const existingRow = drafts.existingStaff[employeeId] || { 
            id: employeeId, 
            originalId: employeeId,
            inputs: { base: 0, add: 0, bonus: 0, hours: 0, days: 0, name: '', type: '', role: '', date: '', notes: '' },
            hasChanges: false,
            calculations: { bruttoTotal: 0, hourlyBase: 0, hourlyTotal: 0, svRate: 0, statusLabel: '---', isMinWageIssue: false, isMiniJobIssue: false },
            isValid: false,
            isLocked: false 
        };

        const newInputs = { ...existingRow.inputs, [field]: value };
        
        if (['base', 'add', 'bonus', 'hours', 'days'].includes(field)) {
            (newInputs as any)[field] = typeof value === 'string' ? (parseFloat(value.replace(',','.')) || 0) : value;
        }

        const calculations = calculateRowMetrics(newInputs, settings);
        const hasChanges = computeExistingStaffDraftHasChanges(employeeId, newInputs, clientData);

        drafts.existingStaff[employeeId] = {
            ...existingRow,
            inputs: newInputs,
            calculations,
            hasChanges
        };

        const newClientData = { ...clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    seedExistingStaffDrafts: (items) => set(state => {
        if (!Array.isArray(items) || items.length === 0) return {};
        const { clientData, settings } = state;
        const drafts = {
            ...clientData.drafts,
            existingStaff: { ...(clientData.drafts?.existingStaff || {}) },
        };

        for (const item of items) {
            const id = String(item?.id || '').trim();
            if (!id) continue;
            const existingRow = drafts.existingStaff[id];
            if (existingRow?.isLocked) continue;

            const baseInputs = existingRow?.inputs || {
                base: 0, add: 0, bonus: 0, hours: 0, days: 0,
                name: '', type: '', role: '', date: '', notes: '',
            };
            const newInputs = { ...baseInputs, ...(item.inputs || {}) };
            for (const numField of ['base', 'add', 'bonus', 'hours', 'days']) {
                if (newInputs[numField] !== undefined && typeof newInputs[numField] === 'string') {
                    newInputs[numField] = parseFloat(String(newInputs[numField]).replace(',', '.')) || 0;
                }
            }
            drafts.existingStaff[id] = {
                id,
                originalId: existingRow?.originalId || id,
                inputs: newInputs,
                calculations: calculateRowMetrics(newInputs, settings),
                hasChanges: computeExistingStaffDraftHasChanges(id, newInputs, clientData),
                isValid: existingRow?.isValid || false,
                isLocked: false,
                lockedBy: existingRow?.lockedBy,
                lockedAt: existingRow?.lockedAt,
            };
        }

        const newClientData = { ...clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    commitDraftState: () => set(state => {
        const newPast = getNewPast(state);
        return { past: newPast, future: [], isDirty: true };
    }),

    resetStaffDraft: (employeeId) => set(state => {
        const drafts = { 
            ...state.clientData.drafts,
            existingStaff: { ...(state.clientData.drafts?.existingStaff || {}) } 
        };
        const target = drafts.existingStaff[employeeId];
        const linkedProdId = String(target?.inputs?.linkedProductiveEmployeeId || '').trim();

        // Paar gemeinsam entfernen: Unprod. → Produktiv; Produktiv → alle verknüpften Unprod.-Anteile
        delete drafts.existingStaff[employeeId];
        if (linkedProdId && drafts.existingStaff[linkedProdId]) {
            delete drafts.existingStaff[linkedProdId];
        }
        for (const [id, d] of Object.entries(drafts.existingStaff)) {
            if (
                (d as any)?.inputs?.role === 'Unprod. Anteil' &&
                String((d as any)?.inputs?.linkedProductiveEmployeeId || '') === String(employeeId)
            ) {
                delete drafts.existingStaff[id];
            }
        }

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    addNewStaffDraft: () => set(state => {
        const drafts = { ...state.clientData.drafts, newStaff: [...(state.clientData.drafts?.newStaff || [])] };
        
        const newId = generateSimpleId();
        const newItem: NewStaffDraftItem = {
            id: newId,
            inputs: {
                date: '',
                name: '',
                type: '',
                role: '',
                base: 0,
                add: 0,
                bonus: 0,
                hours: 0,
                days: 0,
                notes: ''
            },
            calculations: {
                bruttoTotal: 0,
                hourlyBase: 0,
                hourlyTotal: 0,
                svRate: 0,
                statusLabel: '---',
                isMinWageIssue: false,
                isMiniJobIssue: false
            },
            isComplete: false,
            isLocked: false 
        };

        drafts.newStaff.push(newItem);
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    updateNewStaffDraft: (id, field, value) => set(state => {
        const drafts = { ...state.clientData.drafts, newStaff: [...(state.clientData.drafts?.newStaff || [])] };
        const index = drafts.newStaff.findIndex(item => item.id === id);
        
        if (index === -1) return state;

        const currentItem = drafts.newStaff[index];
        const newInputs = { ...currentItem.inputs, [field]: value };

        if (['base', 'add', 'bonus', 'hours', 'days'].includes(field)) {
            (newInputs as any)[field] = typeof value === 'string' ? (parseFloat(value.replace(',','.')) || 0) : value;
        }

        const calculations = calculateRowMetrics(newInputs, state.settings);

        const isExempt = newInputs.type === 'Unproduktiv' || newInputs.type === 'Azubi & Assistenz';
        let isComplete = 
            newInputs.date !== '' &&
            newInputs.name.trim() !== '' &&
            newInputs.type !== '' &&
            newInputs.role !== '' &&
            newInputs.base > 0 &&
            newInputs.hours > 0;
        
        if (!isExempt) {
            if (newInputs.days <= 0) isComplete = false;
        }

        drafts.newStaff[index] = {
            ...currentItem,
            inputs: newInputs,
            calculations,
            isComplete
        };

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    removeNewStaffDraft: (id) => set(state => {
        const drafts = { ...state.clientData.drafts, newStaff: (state.clientData.drafts?.newStaff || []).filter(i => i.id !== id) };
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    addEntrepreneurDraft: () => set(state => {
        const drafts = { ...state.clientData.drafts, entrepreneur: [...(state.clientData.drafts?.entrepreneur || [])] };
        
        const newId = generateSimpleId();
        const newItem: EntrepreneurDraftItem = {
            id: newId,
            inputs: {
                date: '',
                entnahme: 0,
                social: 0,
                umsatz: 0,
                hours: 0,
                days: 0,
                notes: ''
            },
            calculations: {
                totalBelastung: 0
            },
            isComplete: false,
            hasChanges: false,
            isLocked: false 
        };

        drafts.entrepreneur.push(newItem);
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    updateEntrepreneurDraft: (id, field, value) => set(state => {
        const drafts = { ...state.clientData.drafts, entrepreneur: [...(state.clientData.drafts?.entrepreneur || [])] };
        const index = drafts.entrepreneur.findIndex(item => item.id === id);
        
        if (index === -1) return state;

        const currentItem = drafts.entrepreneur[index];
        const newInputs = { ...currentItem.inputs, [field]: value };

        if (['entnahme', 'social', 'umsatz', 'hours', 'days'].includes(field)) {
            (newInputs as any)[field] = typeof value === 'string' ? (parseFloat(value.replace(',','.')) || 0) : value;
        }

        const calculations = calculateEntrepreneurMetrics(newInputs);

        const original = state.clientData.unternehmerData || {};
        let hasChanges = false;
        const isDiff = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) > 0.01;

        if (newInputs.date !== '') {
            if (isDiff(newInputs.entnahme, original.entnahme || 0)) hasChanges = true;
            if (isDiff(newInputs.social, original.sozialkosten || 0)) hasChanges = true;
            if (isDiff(newInputs.umsatz, original.durchschnittsumsatz || 0)) hasChanges = true;
            if (isDiff(newInputs.hours, original.prodWochenstunden || 0)) hasChanges = true;
            if (isDiff(newInputs.days, original.prodWochentage || 0)) hasChanges = true;
            if (newInputs.notes && newInputs.notes.trim() !== '') hasChanges = true;
        }

        let isComplete = 
            newInputs.date !== '' &&
            (newInputs.entnahme > 0 || newInputs.social > 0) &&
            newInputs.hours > 0;

        drafts.entrepreneur[index] = {
            ...currentItem,
            inputs: newInputs,
            calculations,
            isComplete,
            hasChanges
        };

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    removeEntrepreneurDraft: (id) => set(state => {
        const drafts = { 
            ...state.clientData.drafts, 
            entrepreneur: (state.clientData.drafts?.entrepreneur || []).filter(i => i.id !== id) 
        };
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    addClientMasterDraft: () => set(state => {
        const drafts = { ...state.clientData.drafts, clientMaster: [...(state.clientData.drafts?.clientMaster || [])] };
        
        const newId = generateSimpleId();
        const newItem: ClientMasterDraftItem = {
            id: newId,
            inputs: {
                date: '',
                unternehmername: '',
                salonname: '',
                unternehmensform: '',
                strasse: '',
                plz: '',
                ort: '',
                mailadresse: '',
                whatsappNr: '',
                websiteUrl: '',
                socialMedia: '',
                notes: ''
            },
            isComplete: false,
            hasChanges: false,
            isLocked: false 
        };

        drafts.clientMaster.push(newItem);
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    updateClientMasterDraft: (id, field, value) => set(state => {
        const drafts = { ...state.clientData.drafts, clientMaster: [...(state.clientData.drafts?.clientMaster || [])] };
        const index = drafts.clientMaster.findIndex(item => item.id === id);
        
        if (index === -1) return state;

        const currentItem = drafts.clientMaster[index];
        const newInputs = { ...currentItem.inputs, [field]: value };

        const original = state.clientData;
        let hasChanges = false;
        
        const checkField = (key: string, origKey: string = key) => {
            if (newInputs.date !== '') {
                const newVal = (newInputs as any)[key];
                const origVal = (original as any)[origKey] || '';
                if (String(newVal).trim() !== String(origVal).trim()) hasChanges = true;
            }
        };

        checkField('unternehmername');
        checkField('salonname');
        checkField('unternehmensform');
        checkField('strasse');
        checkField('plz');
        checkField('ort');
        checkField('mailadresse');
        checkField('whatsappNr');
        checkField('websiteUrl');
        checkField('socialMedia');
        if (newInputs.notes && newInputs.notes.trim() !== '') hasChanges = true;

        let isComplete = 
            newInputs.date !== '' &&
            newInputs.unternehmername !== '' &&
            newInputs.salonname !== '';

        drafts.clientMaster[index] = {
            ...currentItem,
            inputs: newInputs,
            isComplete,
            hasChanges
        };

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    removeClientMasterDraft: (id) => set(state => {
        const drafts = { 
            ...state.clientData.drafts, 
            clientMaster: (state.clientData.drafts?.clientMaster || []).filter(i => i.id !== id) 
        };
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    addCostDraft: () => set(state => {
        const drafts = { ...state.clientData.drafts, costs: [...(state.clientData.drafts?.costs || [])] };
        
        const newItem: CostDraftItem = {
            id: generateSimpleId(),
            inputs: {
                date: '',
                type: '',
                category: '',
                detail: '',
                method: '',
                prevValue: 0,
                newValue: 0,
                notes: ''
            },
            calculations: {
                effect: 0
            },
            isComplete: false,
            isLocked: false 
        };

        drafts.costs.push(newItem);
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    updateCostDraft: (id, field, value) => set(state => {
        const drafts = { ...state.clientData.drafts, costs: [...(state.clientData.drafts?.costs || [])] };
        const index = drafts.costs.findIndex(item => item.id === id);
        
        if (index === -1) return state;

        const currentItem = drafts.costs[index];
        const newInputs = { ...currentItem.inputs };
        
        (newInputs as any)[field] = value;

        if (['prevValue', 'newValue'].includes(field)) {
            (newInputs as any)[field] = typeof value === 'string' ? (parseFloat(value.replace(',','.')) || 0) : value;
        }

        const effect = calculateCostEffect(newInputs);

        let isComplete = newInputs.date !== '' && newInputs.type !== '';
        
        if (newInputs.type === 'new') {
            if (newInputs.detail.trim() === '' || newInputs.newValue === 0) isComplete = false;
        } else if (newInputs.type === 'flat') {
            if (newInputs.category === '' || newInputs.method === '' || newInputs.newValue === 0) isComplete = false;
        } else if (newInputs.type === 'part') {
            if (newInputs.category === '' || newInputs.detail.trim() === '' || newInputs.newValue === 0) isComplete = false;
        }

        drafts.costs[index] = {
            ...currentItem,
            inputs: newInputs,
            calculations: { effect },
            isComplete
        };

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    removeCostDraft: (id) => set(state => {
        const drafts = { 
            ...state.clientData.drafts, 
            costs: (state.clientData.drafts?.costs || []).filter(i => i.id !== id) 
        };
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    addOpeningHoursDraft: () => set(state => {
        const drafts = { ...state.clientData.drafts, openingHours: [...(state.clientData.drafts?.openingHours || [])] };
        const currentHours = state.clientData.unternehmerData?.geoeffneteStunden || [0,0,0,0,0,0];
        
        const newItem: OpeningHoursDraftItem = {
            id: generateSimpleId(),
            inputs: {
                date: '',
                mon: currentHours[0] || 0,
                tue: currentHours[1] || 0,
                wed: currentHours[2] || 0,
                thu: currentHours[3] || 0,
                fri: currentHours[4] || 0,
                sat: currentHours[5] || 0,
                notes: ''
            },
            isComplete: false,
            hasChanges: false,
            isLocked: false 
        };

        drafts.openingHours.push(newItem);
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    updateOpeningHoursDraft: (id, field, value) => set(state => {
        const drafts = { ...state.clientData.drafts, openingHours: [...(state.clientData.drafts?.openingHours || [])] };
        const index = drafts.openingHours.findIndex(item => item.id === id);
        
        if (index === -1) return state;

        const currentItem = drafts.openingHours[index];
        const newInputs = { ...currentItem.inputs };

        if (['mon', 'tue', 'wed', 'thu', 'fri', 'sat'].includes(field)) {
            (newInputs as any)[field] = typeof value === 'string' ? (parseFloat(value.replace(',','.')) || 0) : value;
        } else {
            (newInputs as any)[field] = value;
        }

        const orig = state.clientData.unternehmerData?.geoeffneteStunden || [0,0,0,0,0,0];
        let hasChanges = false;
        const isDiff = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) > 0.01;
        
        if (newInputs.date !== '') {
            if (isDiff(newInputs.mon, orig[0])) hasChanges = true;
            if (isDiff(newInputs.tue, orig[1])) hasChanges = true;
            if (isDiff(newInputs.wed, orig[2])) hasChanges = true;
            if (isDiff(newInputs.thu, orig[3])) hasChanges = true;
            if (isDiff(newInputs.fri, orig[4])) hasChanges = true;
            if (isDiff(newInputs.sat, orig[5])) hasChanges = true;
            if (newInputs.notes && newInputs.notes.trim() !== '') hasChanges = true;
        }

        const isComplete = newInputs.date !== '' && hasChanges;

        drafts.openingHours[index] = {
            ...currentItem,
            inputs: newInputs,
            isComplete,
            hasChanges
        };

        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),

    removeOpeningHoursDraft: (id) => set(state => {
        const drafts = { 
            ...state.clientData.drafts, 
            openingHours: (state.clientData.drafts?.openingHours || []).filter(i => i.id !== id) 
        };
        const newClientData = { ...state.clientData, drafts };
        saveToLocalStorage({ ...state, clientData: newClientData });
        return { clientData: newClientData, isDirty: true };
    }),
    
    setDraftLockStatus: async (category, id, isLocked, coachName) => {
        const state = get();
        const drafts = { ...state.clientData.drafts };
        
        const getInitials = (name: string) => {
            if (!name) return 'A';
            if (name.includes('Christian')) return 'C';
            if (name.includes('Martina')) return 'M';
            if (name.includes('Jean')) return 'J';
            if (name.includes('Nelly')) return 'N';
            if (name.includes('Omar')) return 'O';
            return name.charAt(0).toUpperCase();
        };

        const getDate = () => {
            const d = new Date();
            return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
        };

        const resolveAuthorName = (name?: string) => {
            if (!name) return 'Admin';
            const initialMap: Record<string, string> = {
                J: 'Jean', M: 'Martina', N: 'Nelly', C: 'Christian', O: 'Omar',
            };
            const trimmed = String(name).trim();
            if (trimmed.length === 1 && initialMap[trimmed.toUpperCase()]) {
                return initialMap[trimmed.toUpperCase()];
            }
            return trimmed;
        };

        const categoryLabels: Record<string, string> = {
            existingStaff: 'Bestandsmitarbeiter',
            newStaff: 'Neuer Mitarbeiter',
            entrepreneur: 'Unternehmer',
            clientMaster: 'Mandantendaten',
            costs: 'Unternehmenskosten',
            openingHours: 'Öffnungszeiten',
        };

        const findDraftItem = (): any => {
            if (category === 'existingStaff') return drafts.existingStaff?.[id];
            if (category === 'newStaff') return (drafts.newStaff || []).find((i: any) => i.id === id);
            if (category === 'entrepreneur') return (drafts.entrepreneur || []).find((i: any) => i.id === id);
            if (category === 'clientMaster') return (drafts.clientMaster || []).find((i: any) => i.id === id);
            if (category === 'costs') return (drafts.costs || []).find((i: any) => i.id === id);
            if (category === 'openingHours') return (drafts.openingHours || []).find((i: any) => i.id === id);
            return null;
        };

        const buildChronikDescription = (draftItem: any): string => {
            const catLabel = categoryLabels[category] || category;
            const inputs = draftItem?.inputs || {};
            const notes = String(inputs.notes || inputs.detail || '').trim();
            const personOrLabel = String(inputs.name || inputs.category || inputs.type || '').trim();

            if (notes && personOrLabel) return `${catLabel} (${personOrLabel}): ${notes}`;
            if (notes) return `${catLabel}: ${notes}`;
            if (personOrLabel) return `${catLabel}: ${personOrLabel}`;
            return `${catLabel} geprüft/übernommen`;
        };

        const lockedBy = isLocked ? getInitials(coachName || 'Admin') : undefined;
        const lockedAt = isLocked ? getDate() : undefined;

        // Chronik-Beschreibung aus Draft VOR dem Lock-Update lesen
        const draftBeforeLock = findDraftItem();

        // Doppelklick / Parallel-Aufruf: kein zweiter Chronik-Eintrag
        const inflightKey = `${category}:${id}`;
        if (isLocked) {
            if (draftBeforeLock?.isLocked) {
                return;
            }
            if (draftLockInflight.has(inflightKey)) {
                return;
            }
            draftLockInflight.add(inflightKey);
        }

        try {
        if (category === 'existingStaff') {
            drafts.existingStaff = { ...drafts.existingStaff };
            if (drafts.existingStaff[id]) {
                drafts.existingStaff[id] = { ...drafts.existingStaff[id], isLocked, lockedBy, lockedAt };
            }
        } else if (category === 'newStaff') {
            drafts.newStaff = (drafts.newStaff || []).map(i => i.id === id ? { ...i, isLocked, lockedBy, lockedAt } : i);
        } else if (category === 'entrepreneur') {
            drafts.entrepreneur = (drafts.entrepreneur || []).map(i => i.id === id ? { ...i, isLocked, lockedBy, lockedAt } : i);
        } else if (category === 'clientMaster') {
            drafts.clientMaster = (drafts.clientMaster || []).map(i => i.id === id ? { ...i, isLocked, lockedBy, lockedAt } : i);
        } else if (category === 'costs') {
            drafts.costs = (drafts.costs || []).map(i => i.id === id ? { ...i, isLocked, lockedBy, lockedAt } : i);
        } else if (category === 'openingHours') {
            drafts.openingHours = (drafts.openingHours || []).map(i => i.id === id ? { ...i, isLocked, lockedBy, lockedAt } : i);
        }

        let aenderungsChronik = Array.isArray(state.clientData.aenderungsChronik)
            ? [...state.clientData.aenderungsChronik]
            : [];

        if (isLocked) {
            aenderungsChronik.push({
                createdAt: lockedAt || getDate(),
                authorName: resolveAuthorName(coachName),
                description: buildChronikDescription(draftBeforeLock),
                draftId: id,
                category,
            });
        }

        // Identische Einträge entdoppeln (bestehende Duplikate + Race-Schutz)
        const seenChronik = new Set<string>();
        aenderungsChronik = aenderungsChronik.filter((e: any) => {
            const key = [
                e?.createdAt || '',
                e?.authorName || '',
                e?.description || '',
                e?.draftId || '',
                e?.category || '',
            ].join('|');
            if (seenChronik.has(key)) return false;
            seenChronik.add(key);
            return true;
        });

        const newClientData = { ...state.clientData, drafts, aenderungsChronik };
        set({ clientData: newClientData, isDirty: true, cloudWriteLock: true });
        saveToLocalStorage({ ...get(), clientData: newClientData, isDirty: true });
        if (typeof window !== 'undefined') {
            (window as any).isSystemDirty = true;
        }

        try {
            const { persistClientDataImmediate } = await import('../../config/firebaseSync');
            const success = await persistClientDataImmediate();
            set({ cloudWriteLock: false, isDirty: !success });
            if (typeof window !== 'undefined') {
                (window as any).isSystemDirty = !success;
            }
        } catch (err) {
            console.error('setDraftLockStatus: Cloud-Persist fehlgeschlagen', err);
            set({ cloudWriteLock: false, isDirty: true });
        }
        } finally {
            if (isLocked) {
                draftLockInflight.delete(inflightKey);
            }
        }
    },

    setPerformanceLock: async (domain, targetId, isLocked, metricsSnapshot, adminId, lockContext) => {
        set({ cloudWriteLock: true });

        const stateBefore = get();
        const newPast = getNewPast(stateBefore);
        const newClientData = applyPerformanceLockToClientData(
            stateBefore.clientData,
            domain,
            targetId,
            isLocked,
            metricsSnapshot,
            adminId,
            lockContext
        );

        const verifyContext = (
            domain === 'mitarbeiter'
            && targetId
            && lockContext?.halbjahrKey !== undefined
            && lockContext?.monatIndex !== undefined
        ) ? {
            employeeId: targetId,
            halbjahrKey: lockContext.halbjahrKey,
            monatIndex: lockContext.monatIndex,
        } : undefined;

        if (isLocked && verifyContext && metricsSnapshot) {
            const normalizedSnapshot = normalizeMonatsFestsetzungSnapshot(metricsSnapshot);
            const lockedAt = getPerformanceLockDate();
            const lockedBy = adminId || 'Admin';

            stashPendingFestsetzung(
                verifyContext.employeeId,
                verifyContext.halbjahrKey,
                verifyContext.monatIndex,
                normalizedSnapshot
            );
            saveLocalFestsetzungBackup(
                verifyContext.employeeId,
                verifyContext.halbjahrKey,
                verifyContext.monatIndex,
                normalizedSnapshot,
                { lockedAt, lockedBy }
            );
        } else if (!isLocked && verifyContext) {
            clearPendingFestsetzung(
                verifyContext.employeeId,
                verifyContext.halbjahrKey,
                verifyContext.monatIndex
            );
            removeLocalFestsetzungBackup(
                verifyContext.employeeId,
                verifyContext.halbjahrKey,
                verifyContext.monatIndex
            );
        }

        set({
            clientData: newClientData,
            past: newPast,
            future: [],
            isDirty: true,
            cloudWriteLock: true,
        });

        saveToLocalStorage({
            ...get(),
            clientData: newClientData,
            past: newPast,
            future: [],
            isDirty: true,
        });

        if (typeof window !== 'undefined') {
            (window as any).isSystemDirty = true;
        }

        try {
            const { persistClientDataImmediate } = await import('../../config/firebaseSync');
            const normalizedSnapshot = (isLocked && metricsSnapshot)
                ? normalizeMonatsFestsetzungSnapshot(metricsSnapshot)
                : undefined;
            const success = await persistClientDataImmediate(
                verifyContext
                    ? {
                        verifyContext,
                        snapshot: normalizedSnapshot,
                        lockedAt: getPerformanceLockDate(),
                        lockedBy: adminId || 'Admin',
                        isLocked,
                    }
                    : undefined
            );

            const current = get();
            set({
                cloudWriteLock: false,
                isDirty: !success,
            });

            saveToLocalStorage({
                ...current,
                isDirty: !success,
            });

            if (success) {
                if (verifyContext) {
                    clearPendingFestsetzung(
                        verifyContext.employeeId,
                        verifyContext.halbjahrKey,
                        verifyContext.monatIndex
                    );
                }
                if (typeof window !== 'undefined') {
                    (window as any).isSystemDirty = false;
                }
            }
        } catch (error) {
            console.error('setPerformanceLock: Immediate Firebase persist failed', error);
            set({ cloudWriteLock: false, isDirty: true });
        }
    }
});